<template>
	<div>
	<div class="container-xl py-3">

		<!-- Internal data lives inside the results card as the left-most
			column (see <section v-if="cwpeData"> below). There is no outer
			aside / no flex layout at the page level — every state (empty
			hero, loading skeleton, loaded results) is a single card. -->

			<!-- Empty-state hero: shown until the rep picks a muni.
				The full two-input form is gone; only autocomplete + helper text. -->
			<div v-if="!selectedMuniId" class="d-flex justify-content-center" style="padding:48px 16px;">
				<div class="card shadow-sm w-100" style="max-width:560px;border-radius:8px;">
					<div class="card-body text-center" style="padding:32px 28px;">
						<div class="cwpe-label" style="font-size:11px;letter-spacing:0.05em;color:#1d4ed8;">Start a lookup</div>
						<h2 class="mt-2 mb-1" style="font-size:20px;font-weight:600;color:#0f172a;">Find a municipality</h2>
						<p class="mb-4" style="font-size:13px;color:#64748b;">
							Search by name across our municipalities table. Pick from the dropdown to load pricing, internal data, and peer comparisons.
						</p>
						<div class="position-relative text-start" ref="muniWrap">
							<input
								ref="muniInput"
								v-model="form.name"
								type="text"
								class="form-control"
								autocomplete="off"
								placeholder="e.g. Sturgeon Bay"
								@input="onMuniInput"
								@focus="onMuniFocus"
								@blur="onMuniBlur"
								@keydown="onMuniKeydown"
							/>
							<div
								v-if="showDropdown"
								class="position-absolute w-100 border rounded shadow"
								style="top:100%;z-index:1000;background:white;max-height:280px;overflow-y:auto;margin-top:2px;"
							>
								<div v-if="searching" class="px-3 py-3 text-muted small">Searching…</div>
								<div v-else-if="!suggestions.length" class="px-3 py-3 text-muted small">No matches found.</div>
								<ul v-else class="list-unstyled mb-0 py-1">
									<li
										v-for="(s, i) in suggestions"
										:key="s.id"
										@mousedown.prevent="selectSuggestion(s)"
										@mouseenter="activeIdx = i"
										class="px-3 py-2 d-flex align-items-baseline justify-content-between gap-2 cwpe-suggestion-item"
										:class="activeIdx === i ? 'cwpe-suggestion-active' : ''"
										style="cursor:pointer;font-size:0.875rem;"
									>
										<span class="text-truncate">
											{{ s.full_name || s.name }}<span class="text-muted">, {{ s.state }}</span>
											<span v-if="s.county" class="text-muted ms-1 small">· {{ s.county }}</span>
										</span>
									</li>
								</ul>
							</div>
						</div>
						<p v-if="lookupError" class="mt-3 mb-0" style="color:#e11d48;font-size:13px;">{{ lookupError }}</p>
					</div>
				</div>
			</div>

			<!-- Loading skeleton — runs from the moment a muni is picked until
				cwpeData lands. We block the rep on a single visual focus
				(the centered "Looking for data…" overlay) instead of letting
				them stare at half-rendered partial state. -->
			<section v-if="selectedMuniId && !cwpeData" class="card shadow-sm overflow-hidden position-relative cwpe-skeleton-card" style="border-radius:8px;">
				<div class="row g-0">
					<!-- Left: internal data column placeholder -->
					<div class="col-lg-3 p-4 d-none d-md-block" style="background:#f8fafc;border-right:1px solid #e2e8f0;">
						<div class="cwpe-skel mb-3" style="height:14px;width:55%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:80%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:65%;"></div>
						<div class="cwpe-skel mb-4" style="height:14px;width:75%;"></div>
						<div class="cwpe-skel mb-2" style="height:60px;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:70%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:50%;"></div>
					</div>
					<!-- Middle: main content placeholder -->
					<div class="col-lg-6 p-4">
						<div class="cwpe-skel mb-3" style="height:28px;width:55%;"></div>
						<div class="cwpe-skel mb-4" style="height:14px;width:35%;"></div>
						<div class="cwpe-skel mb-4" style="height:80px;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:85%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:70%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:90%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:60%;"></div>
						<div class="cwpe-skel mb-4" style="height:14px;width:80%;"></div>
						<div class="cwpe-skel mb-2" style="height:120px;"></div>
					</div>
					<!-- Right: meta sidebar placeholder -->
					<div class="col-lg-3 p-4" style="background:#f8fafc;border-left:1px solid #e2e8f0;">
						<div class="cwpe-skel mb-3" style="height:160px;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:70%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:90%;"></div>
						<div class="cwpe-skel mb-2" style="height:14px;width:60%;"></div>
						<div class="cwpe-skel mb-4" style="height:14px;width:80%;"></div>
						<div class="cwpe-skel mb-2" style="height:80px;"></div>
					</div>
				</div>
				<div class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
					style="background:rgba(255,255,255,0.55);backdrop-filter:blur(2px);">
					<div class="text-center">
						<div class="spinner-border text-primary mb-3" role="status" style="width:32px;height:32px;border-width:3px;">
							<span class="visually-hidden">Loading…</span>
						</div>
						<div style="font-size:15px;font-weight:600;color:#1d4ed8;letter-spacing:0.01em;">Looking for data…</div>
						<div style="font-size:12px;color:#64748b;margin-top:4px;">Pulling Census, mc2 internals, peer pricing</div>
					</div>
				</div>
			</section>

			<!-- Results -->
			<section v-if="cwpeData" class="card shadow-sm overflow-hidden" style="border-radius:8px;">
				<div class="row g-0">

					<!-- ── Internal data column (merged from old left sidebar) ─ -->
					<aside class="col-lg-3 border-end d-none d-md-block" style="background:#f8fafc;padding:24px;">
						<div class="cwpe-label mb-3">Internal data</div>
						<div v-if="internalData">
							<!-- Open-in-mc2 header link — full muni record opens in a new tab -->
							<div class="mb-3 d-flex align-items-baseline justify-content-between gap-2">
								<a :href="`/municipalities/edit/${internalData.municipality.id}`" target="_blank" rel="noreferrer"
									class="fw-medium text-decoration-none text-truncate" style="color:#1d4ed8;font-size:13px;flex:1;min-width:0;"
									:title="`Open ${internalData.municipality.full_name || internalData.municipality.name} in mc2`">
									↗ Open in mc2
								</a>
								<span v-if="internalData.municipality.population" style="font-size:11px;color:#64748b;flex-shrink:0;">
									pop {{ Number(internalData.municipality.population).toLocaleString() }}
								</span>
							</div>

							<!-- Status chips -->
							<div class="d-flex flex-wrap gap-1 mb-3" style="font-size:10px;">
								<span v-if="internalData.client && internalData.client.tw_client" class="rounded fw-semibold px-2 py-0" style="background:#dbeafe;color:#1e3a8a;" title="Has an active mc2 Client row with TW = true">TW client</span>
								<span v-if="internalData.client && internalData.client.hg_client" class="rounded fw-semibold px-2 py-0" style="background:#ede9fe;color:#5b21b6;" title="Has an active mc2 Client row with HG = true">HG client</span>
								<span v-if="internalData.client && internalData.client.tk_client" class="rounded fw-semibold px-2 py-0" style="background:#fce7f3;color:#9d174d;" title="Has an active mc2 Client row with TK = true">TK client</span>
								<span v-if="!internalData.client && (internalData.municipality.tw_customer || internalData.municipality.hg_customer)" class="rounded fw-semibold px-2 py-0" style="background:#f1f5f9;color:#334155;" title="No mc2 Client row, but the municipality is flagged as a customer in the municipalities table.">flagged customer</span>
								<span v-if="!internalData.client && !internalData.municipality.tw_customer && !internalData.municipality.hg_customer" class="rounded fw-semibold px-2 py-0" style="background:#fef3c7;color:#92400e;" title="Not a TW/HG/TK customer — no Client row, no customer flag in mc2.">Not a customer</span>
								<span v-if="renewalCountdown" class="rounded fw-semibold px-2 py-0" :style="renewalChipStyle" title="Days until/since contract_ends_at">{{ renewalCountdown }}</span>
								<span v-if="internalData.ar_escalation" class="rounded fw-semibold px-2 py-0" style="background:#fee2e2;color:#991b1b;" title="Active accounts-receivable escalation row (not yet completed).">⚠ AR escalation: {{ internalData.ar_escalation.current_stage }}</span>
								<span v-if="internalData.engagement && internalData.engagement.disable_campaigns" class="rounded fw-semibold px-2 py-0" style="background:#fee2e2;color:#991b1b;" title="municipalities.disable_campaigns = true — do not contact for outreach">🚫 DNC</span>
							</div>

							<!-- Existing client snapshot -->
							<div v-if="internalData.client" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Existing client</div>
								<div class="mt-1" style="color:#334155;">
									<div v-if="internalData.client.package"><span style="color:#64748b;">Package:</span> <strong>{{ internalData.client.package }}</strong></div>
									<div v-if="internalData.client.theme"><span style="color:#64748b;">Theme:</span> {{ internalData.client.theme }}</div>
									<div v-if="internalData.client.client_since"><span style="color:#64748b;">Client since:</span> {{ fmtCompactDate(internalData.client.client_since) }}</div>
									<div v-if="internalData.client.contract_ends_at"><span style="color:#64748b;">Contract ends:</span> {{ fmtCompactDate(internalData.client.contract_ends_at) }}</div>
									<div v-if="internalData.client.staging_url" class="text-truncate"><span style="color:#64748b;">Staging:</span>
										<a :href="internalData.client.staging_url" target="_blank" rel="noreferrer" style="color:#1d4ed8;text-decoration:none;">{{ hostnameOf(internalData.client.staging_url) }}</a>
									</div>
								</div>
							</div>

							<!-- Currently paying -->
							<div v-if="internalData.billing && internalData.billing.amount" class="mb-3 rounded p-2" style="background:rgba(236,253,245,0.5);border:1px solid #a7f3d0;font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;color:#047857;">Currently paying</div>
								<div class="d-flex align-items-baseline gap-2 mt-1">
									<span class="fw-bold" style="font-size:16px;color:#064e3b;">${{ Number(internalData.billing.amount).toLocaleString('en-US', { maximumFractionDigits: 0 }) }}</span>
									<span style="color:#047857;font-size:11px;">{{ internalData.billing.translated_frequency || internalData.billing.frequency || '—' }}</span>
								</div>
								<div v-if="internalData.billing.description" style="font-size:11px;color:#475569;" class="text-truncate">{{ internalData.billing.description }}</div>
								<div v-if="internalData.billing.next_invoice_date" style="font-size:10px;color:#64748b;">Next invoice: {{ fmtCompactDate(internalData.billing.next_invoice_date) }}</div>
							</div>

							<!-- Active add-ons -->
							<div v-if="activeAddons.length" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Active add-ons</div>
								<div class="d-flex flex-wrap gap-1 mt-1">
									<span v-for="a in activeAddons" :key="a"
										class="rounded fw-medium px-2 py-0" style="font-size:10px;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;">
										{{ a }}
									</span>
								</div>
							</div>

							<!-- Detected platform -->
							<div v-if="vendorList.length" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Detected platform</div>
								<div class="mt-1 d-flex flex-column gap-1">
									<div v-for="v in vendorList" :key="v.name" class="d-flex align-items-center gap-2">
										<span v-if="v.color" class="d-inline-block rounded" :style="{ width:'8px',height:'8px',background: v.color }"></span>
										<span style="color:#0f172a;">{{ v.name }}</span>
										<span v-if="v.type" style="color:#94a3b8;font-size:10px;">{{ v.type }}</span>
									</div>
								</div>
							</div>

							<!-- Tech stack -->
							<div v-if="techStack.length" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Tech stack</div>
								<div class="d-flex flex-wrap gap-1 mt-1">
									<span v-for="t in techStack" :key="t"
										class="rounded fw-medium px-2 py-0" style="font-size:10px;background:#eff6ff;color:#1e40af;border:1px solid #bfdbfe;">
										{{ t }}
									</span>
								</div>
							</div>

							<!-- Address / phone / email -->
							<div v-if="hasAddressBlock" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Address</div>
								<div class="mt-1" style="color:#334155;">
									<div v-if="internalData.municipality.address">{{ internalData.municipality.address }}</div>
									<div v-if="internalData.municipality.zip">{{ internalData.municipality.zip }}</div>
									<div v-if="internalData.municipality.phone"><span style="color:#64748b;">Phone:</span> {{ internalData.municipality.phone }}</div>
									<div v-if="internalData.municipality.email" class="text-truncate"><span style="color:#64748b;">Email:</span>
										<a :href="`mailto:${internalData.municipality.email}`" style="color:#1d4ed8;text-decoration:none;">{{ internalData.municipality.email }}</a>
									</div>
								</div>
							</div>

							<!-- Contacts: 2 visible, expand for more -->
							<div v-if="internalData.contacts && internalData.contacts.length" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label d-flex justify-content-between align-items-baseline" style="font-size:10px;letter-spacing:0.05em;">
									<span>Contacts</span>
									<span style="color:#94a3b8;font-weight:400;">{{ internalData.contacts.length }} total</span>
								</div>
								<ul class="list-unstyled mb-0 mt-1">
									<li v-for="c in visibleContacts" :key="c.id" class="rounded mb-1 p-2" style="background:white;border:1px solid #e2e8f0;">
										<div class="d-flex align-items-baseline justify-content-between gap-2">
											<span class="fw-medium" style="color:#0f172a;">{{ contactName(c) }}</span>
											<span v-if="c.title" class="text-truncate" style="font-size:10px;color:#64748b;">{{ c.title }}</span>
										</div>
										<div v-if="c.email" class="text-truncate" style="font-size:11px;">
											<a :href="`mailto:${c.email}`" style="color:#1d4ed8;text-decoration:none;">{{ c.email }}</a>
											<span v-if="c.unsubscribed" class="ms-1" style="color:#be123c;" title="Unsubscribed">✗</span>
											<span v-else-if="c.norbert_bounced" class="ms-1" style="color:#ca8a04;" title="Bounced">↩</span>
										</div>
										<div v-if="c.phone" style="font-size:11px;color:#475569;">{{ c.phone }}</div>
									</li>
								</ul>
								<button type="button" @click="contactsExpanded = !contactsExpanded"
									class="btn btn-link btn-sm p-0" style="font-size:11px;color:#1d4ed8;text-decoration:none;">
									{{ contactsExpanded ? '▴ hide contacts' : `▾ show ${internalData.contacts.length} contact${internalData.contacts.length === 1 ? '' : 's'}` }}
								</button>
							</div>

							<!-- Departments -->
							<div v-if="internalData.departments && internalData.departments.length" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Departments</div>
								<ul class="list-unstyled mb-0 mt-1">
									<li v-for="d in visibleDepartments" :key="d.id" class="d-flex align-items-baseline justify-content-between gap-2 mb-1">
										<span class="fw-medium text-truncate" style="color:#334155;">{{ d.name }}</span>
										<a v-if="d.email" :href="`mailto:${d.email}`" class="flex-shrink-0 text-truncate" style="font-size:11px;color:#1d4ed8;text-decoration:none;max-width:140px;">{{ d.email }}</a>
									</li>
								</ul>
								<button v-if="internalData.departments.length > 3" type="button" @click="departmentsExpanded = !departmentsExpanded"
									class="btn btn-link btn-sm p-0" style="font-size:11px;color:#1d4ed8;text-decoration:none;">
									{{ departmentsExpanded ? '▴ show less' : `▾ show ${internalData.departments.length - 3} more` }}
								</button>
							</div>

							<!-- Engagement -->
							<div v-if="hasEngagement" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Engagement</div>
								<div class="mt-1" style="color:#475569;">
									<div v-if="internalData.engagement.last_email_date">Last email: {{ fmtRelative(internalData.engagement.last_email_date) }}</div>
									<div v-if="internalData.engagement.last_mail_date">Last mail: {{ fmtRelative(internalData.engagement.last_mail_date) }}</div>
									<div v-if="internalData.engagement.last_delivery">
										Last delivery:
										<span style="color:#334155;">{{ internalData.engagement.last_delivery.campaign_name || '—' }}</span>
										<span class="ms-1 rounded fw-medium px-1 text-capitalize" style="font-size:9px;background:#f1f5f9;color:#475569;">{{ campaignTypeLabel(internalData.engagement.last_delivery.type) }}</span>
										<span style="color:#94a3b8;"> · {{ internalData.engagement.last_delivery.status }}</span>
										<span v-if="internalData.engagement.last_delivery.send_at" style="color:#94a3b8;"> · {{ fmtCompactDate(internalData.engagement.last_delivery.send_at) }} ({{ fmtRelative(internalData.engagement.last_delivery.send_at) }})</span>
									</div>
									<div v-if="internalData.engagement.active_sequence">
										In sequence: <strong style="color:#0f172a;">{{ internalData.engagement.active_sequence.campaign_name }}</strong>
									</div>
								</div>

								<!-- Collapsible full campaign history — same UX as contacts -->
								<div v-if="campaignsCount > 0" class="mt-2">
									<button type="button" @click="campaignsExpanded = !campaignsExpanded"
										class="btn btn-link btn-sm p-0 text-decoration-none" style="font-size:11px;color:#1d4ed8;">
										{{ campaignsExpanded
											? '▴ hide campaigns'
											: `▾ show ${campaignsCount} campaign${campaignsCount === 1 ? '' : 's'}` }}
									</button>
									<ul v-if="campaignsExpanded" class="list-unstyled mb-0 mt-1">
										<li v-for="d in internalData.engagement.recent_campaigns" :key="d.id"
											class="rounded mb-1 p-2" style="background:white;border:1px solid #e2e8f0;">
											<div class="d-flex align-items-baseline justify-content-between gap-2">
												<span class="fw-medium text-truncate" style="color:#0f172a;flex:1;min-width:0;" :title="d.campaign_name">{{ d.campaign_name || '—' }}</span>
												<span class="rounded fw-medium px-1 flex-shrink-0 text-capitalize" style="font-size:9px;background:#f1f5f9;color:#475569;">{{ campaignTypeLabel(d.type) }}</span>
											</div>
											<div class="d-flex align-items-baseline justify-content-between gap-2 mt-1" style="font-size:11px;">
												<span style="color:#64748b;">{{ d.send_at ? fmtCompactDate(d.send_at) : '—' }}</span>
												<span :style="{ color: deliveryStatusColor(d.status) }">{{ d.status }}</span>
											</div>
										</li>
									</ul>
								</div>
							</div>

							<!-- ADA / risk -->
							<div v-if="internalData.ada_audit || internalData.pdf_scan_summary" class="mb-3 rounded p-2" style="background:#fefce8;border:1px solid #fde68a;font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;color:#92400e;">ADA / risk</div>
								<div v-if="internalData.ada_audit" class="mt-1">
									<div>Compliance: <strong :style="{ color: adaScoreColor(internalData.ada_audit.compliance_score) }">{{ internalData.ada_audit.compliance_score != null ? internalData.ada_audit.compliance_score : '—' }}%</strong></div>
									<div v-if="internalData.ada_audit.total_errors != null" style="font-size:11px;color:#475569;">
										{{ internalData.ada_audit.total_errors }} errors · {{ internalData.ada_audit.total_warnings || 0 }} warnings · {{ internalData.ada_audit.total_pages_scanned || 0 }} pages
									</div>
								</div>
								<div v-if="internalData.pdf_scan_summary && internalData.pdf_scan_summary.scanned_count > 0" class="mt-1" style="font-size:11px;color:#475569;">
									PDFs: {{ internalData.pdf_scan_summary.scanned_count }} scanned ·
									<span v-if="internalData.pdf_scan_summary.critical" style="color:#be123c;font-weight:600;">{{ internalData.pdf_scan_summary.critical }} critical</span>
									<span v-if="internalData.pdf_scan_summary.major" style="color:#92400e;font-weight:600;"> · {{ internalData.pdf_scan_summary.major }} major</span>
									<span v-if="internalData.pdf_scan_summary.minor" style="color:#475569;"> · {{ internalData.pdf_scan_summary.minor }} minor</span>
								</div>
							</div>

							<!-- County -->
							<div v-if="internalData.county" class="mb-3" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">County</div>
								<div class="mt-1" style="color:#334155;">
									<div><strong>{{ internalData.county.name }}</strong><span v-if="internalData.county.county_seat" style="color:#94a3b8;"> · seat: {{ internalData.county.county_seat }}</span></div>
									<div v-if="internalData.county.population" style="font-size:11px;color:#64748b;">Pop {{ Number(internalData.county.population).toLocaleString() }}<span v-if="internalData.county.total_municipalities"> · {{ internalData.county.total_municipalities }} munis</span></div>
									<div v-if="internalData.county.is_customer" style="font-size:11px;"><span class="rounded fw-semibold px-1" style="background:#dcfce7;color:#166534;font-size:10px;">county is customer</span></div>
									<div v-if="internalData.county.competitor" style="font-size:11px;color:#64748b;">County vendor: {{ internalData.county.competitor }}</div>
								</div>
							</div>

							<!-- CRM links — URLs match the patterns in LPDataTableTbody / ClientEdit -->
							<div v-if="hasCrmLinks" class="d-flex flex-wrap gap-1 mb-3">
								<a v-if="internalData.client && internalData.client.salesforce_account_id"
									:href="salesforceUrl(internalData.client.salesforce_account_id)"
									target="_blank" rel="noreferrer"
									class="btn btn-sm" style="border:1px solid #e2e8f0;background:white;color:#0078d4;font-size:11px;padding:2px 8px;">Salesforce</a>
								<a v-if="internalData.client && internalData.client.freshbooks_client_id"
									:href="freshbooksUrl(internalData.client.freshbooks_client_id)"
									target="_blank" rel="noreferrer"
									class="btn btn-sm" style="border:1px solid #e2e8f0;background:white;color:#0c8a4f;font-size:11px;padding:2px 8px;">FreshBooks</a>
								<a v-if="internalData.client && internalData.client.juniper_id"
									:href="`/juniper/clients/edit/${internalData.client.juniper_id}`" target="_blank"
									class="btn btn-sm" style="border:1px solid #e2e8f0;background:white;color:#92400e;font-size:11px;padding:2px 8px;">Juniper</a>
								<a v-if="internalData.client && internalData.client.dropbox_id && dropboxAppUrl"
									:href="dropboxUrl(internalData.client.dropbox_id)" target="_blank" rel="noreferrer"
									class="btn btn-sm" style="border:1px solid #e2e8f0;background:white;color:#0061ff;font-size:11px;padding:2px 8px;">Dropbox</a>
							</div>

							<!-- Internal notes -->
							<div v-if="internalData.internal_notes && internalData.internal_notes.length" class="mb-2" style="font-size:12px;">
								<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;">Internal notes</div>
								<ul class="list-unstyled mb-0 mt-1">
									<li v-for="n in visibleNotes" :key="n.id" class="rounded mb-1 p-2" style="background:white;border:1px solid #e2e8f0;font-size:11px;">
										<div class="text-truncate" style="color:#334155;" :title="n.note">{{ n.note }}</div>
										<div style="font-size:10px;color:#94a3b8;">{{ n.entry_user || '—' }} · {{ fmtCompactDate(n.created_at) }}</div>
									</li>
								</ul>
								<button v-if="internalData.internal_notes.length > 2" type="button" @click="notesExpanded = !notesExpanded"
									class="btn btn-link btn-sm p-0" style="font-size:11px;color:#1d4ed8;text-decoration:none;">
									{{ notesExpanded ? '▴ show less' : `▾ show ${internalData.internal_notes.length - 2} more` }}
								</button>
							</div>
						</div>
						<div v-else>
							<!-- internalData hasn't landed yet (cwpeData came first); mini-skeleton placeholder -->
							<div class="cwpe-skel mb-2" style="height:14px;width:80%;"></div>
							<div class="cwpe-skel mb-2" style="height:14px;width:60%;"></div>
							<div class="cwpe-skel mb-2" style="height:14px;width:90%;"></div>
							<div class="cwpe-skel mb-4" style="height:14px;width:70%;"></div>
							<div class="cwpe-skel mb-2" style="height:60px;"></div>
						</div>
					</aside>

					<!-- ── Middle: main content ─────────────────────────────── -->
					<div class="col-lg-6 p-4">

						<!-- Municipality title + tier -->
						<div class="d-flex align-items-start justify-content-between mb-4 gap-3">
							<div>
								<h2 @dblclick="censusModalOpen = true"
									title="Double-click for full Census details"
									style="font-size:24px;font-weight:600;color:#0f172a;margin-bottom:4px;cursor:pointer;user-select:none;">{{ muniDisplayName }}</h2>
								<p style="font-size:14px;color:#64748b;margin-bottom:0;">
									{{ countyDisplay || '—' }}, {{ form.state }}
								</p>
							</div>
							<span v-if="pricingResult" class="flex-shrink-0" :class="tierBadgeClass"
								style="padding:4px 12px;font-size:12px;font-weight:500;border-radius:9999px;">
								{{ tierLabel }} tier
							</span>
						</div>

						<!-- FIPS — uses the canonical municipalities.fips column -->
						<div
							v-if="displayFips"
							class="rounded mb-3 border"
							style="background:#eff6ff;border-color:#bfdbfe!important;padding:16px;"
						>
							<div class="text-center">
								<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;font-weight:500;color:#1d4ed8;margin-bottom:4px;">
									FIPS
								</div>
								<div style="font-size:24px;font-family:ui-monospace,monospace;font-weight:600;color:#1e3a8a;letter-spacing:-0.025em;margin-bottom:0;">
									{{ displayFips }}
									<span style="margin-left:8px;font-size:16px;font-weight:400;color:#1d4ed8;font-family:inherit;">{{ muniDisplayName }}, {{ form.state }}</span>
								</div>
							</div>
							<div v-if="fipsBreakdown.length"
								class="d-flex flex-wrap justify-content-center gap-3 mt-3 pt-2"
								style="border-top:1px solid #dbeafe;font-size:11px;color:#1d4ed8;">
								<div v-for="part in fipsBreakdown" :key="part.label">
									<span style="color:rgba(29,78,216,0.7);">{{ part.label }}:</span>
									<span style="font-family:ui-monospace,monospace;font-weight:500;margin-left:4px;color:#1e3a8a;">{{ part.value }}</span>
								</div>
							</div>
						</div>

						<!-- Population & 5-yr trend -->
						<div class="card mb-3" style="border-radius:6px;">
							<div class="card-body" style="padding:16px;">
								<div class="d-flex justify-content-between align-items-baseline mb-3">
									<div class="cwpe-label">Population &amp; 5-yr trend</div>
									<svg v-if="sparklinePath" width="120" height="28" style="overflow:visible;flex-shrink:0;">
										<path
											:d="sparklinePath"
											fill="none"
											:stroke="(cwpeData.population_trend_pct || 0) >= 0 ? '#059669' : '#dc2626'"
											stroke-width="1.5"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</div>
								<div class="row g-0">
									<div class="col-4 pe-3">
										<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;margin-bottom:4px;font-weight:400;">Municipality</div>
										<div style="font-size:18px;font-weight:600;color:#0f172a;">{{ fmt(muniPopDisplay) }}</div>
										<div v-if="cwpeData.population_trend_pct != null" style="font-size:12px;font-weight:500;" :class="trendColor(cwpeData.population_trend_pct)">
											{{ trendArrow(cwpeData.population_trend_pct) }} {{ pct(cwpeData.population_trend_pct) }}
										</div>
										<div v-else-if="muniPopDisplay != null && !cwpeData.population" style="font-size:10px;color:#94a3b8;font-style:italic;">from mc2 records</div>
									</div>
									<div class="col-4 px-3 border-start">
										<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;margin-bottom:4px;font-weight:400;">County</div>
										<div style="font-size:18px;font-weight:600;color:#0f172a;">{{ fmt(countyPop) }}</div>
										<div v-if="countyTrend != null" style="font-size:12px;font-weight:500;" :class="trendColor(countyTrend)">
											{{ trendArrow(countyTrend) }} {{ pct(countyTrend) }}
										</div>
									</div>
									<div class="col-4 ps-3 border-start">
										<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;margin-bottom:4px;font-weight:400;">State</div>
										<div style="font-size:18px;font-weight:600;color:#0f172a;">{{ fmt(statePop) }}</div>
										<div v-if="stateTrend != null" style="font-size:12px;font-weight:500;" :class="trendColor(stateTrend)">
											{{ trendArrow(stateTrend) }} {{ pct(stateTrend) }}
										</div>
									</div>
								</div>
								<div v-if="countyTrend != null && stateTrend != null" class="mt-2 small">
									<span
										v-if="(cwpeData.population_trend_pct || 0) > countyTrend && (cwpeData.population_trend_pct || 0) > stateTrend"
										style="color:#047857;font-weight:500;"
									>Outgrowing county and state — positive pricing factor.</span>
									<span
										v-else-if="(cwpeData.population_trend_pct || 0) < countyTrend && (cwpeData.population_trend_pct || 0) < stateTrend"
										style="color:#be123c;font-weight:500;"
									>Below county and state growth — negative pricing factor.</span>
									<span v-else style="color:#64748b;">Tracks roughly with the region.</span>
								</div>
							</div>
						</div>

						<!-- SNAP rate -->
						<div class="card mb-3" style="border-radius:6px;">
							<div class="card-body" style="padding:16px;">
								<div class="d-flex justify-content-between align-items-baseline mb-3">
									<div class="cwpe-label">SNAP rate</div>
									<div style="font-size:10px;color:#94a3b8;">high &gt;12% · low &lt;8%</div>
								</div>
								<div v-if="cwpeData.snap_rate == null" class="text-muted small fst-italic">SNAP data unavailable.</div>
								<template v-else>
									<div class="row g-0">
										<div class="col-6 pe-4">
											<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;margin-bottom:4px;font-weight:400;">
												{{ cwpeData.snap_level ? `${cwpeData.snap_level}-level` : 'Municipality' }}
											</div>
											<div style="font-size:18px;font-weight:600;" :class="snapColor">{{ pct(cwpeData.snap_rate) }}</div>
										</div>
										<div v-if="cwpeData.state_snap_rate != null" class="col-6 ps-4 border-start">
											<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;margin-bottom:4px;font-weight:400;">State average</div>
											<div style="font-size:18px;font-weight:600;" :class="snapColor">{{ pct(cwpeData.state_snap_rate) }}</div>
										</div>
									</div>
									<div class="mt-2 small">
										<span v-if="cwpeData.snap_rate >= 0.12" style="color:#be123c;font-weight:500;">
											Above the 12% threshold — negative pricing factor.
										</span>
										<span v-else-if="cwpeData.snap_rate <= 0.08" style="color:#047857;font-weight:500;">
											Below 8% — favorable pricing factor.
										</span>
										<span v-else style="color:#b45309;font-weight:500;">
											In the mid range (8–12%) — neutral.
										</span>
									</div>
								</template>
							</div>
						</div>

						<!-- First-year summary + adjustment pills -->
						<div v-if="pricingResult" class="mb-3" style="background:#f8fafc;border-radius:6px;padding:16px;">
							<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:8px;">
								<div>
									<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#64748b;margin-bottom:4px;">First-year total</div>
									<div style="font-size:30px;font-weight:600;color:#0f172a;">
										{{ cents(priceRangeMin) }}
										<span v-if="priceRangeMax !== priceRangeMin"> – {{ cents(priceRangeMax) }}</span>
									</div>
									<div style="font-size:11px;color:#64748b;">Setup + annual, charged at signup</div>
									<div style="margin-top:12px;">
										<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:rgba(4,120,87,0.8);margin-bottom:4px;">Annual thereafter</div>
										<div style="font-size:30px;font-weight:600;color:#065f46;">
											{{ cents(annualRangeMin) }}
											<span v-if="annualRangeMax !== annualRangeMin"> – {{ cents(annualRangeMax) }}</span>
											<span style="font-size:16px;font-weight:400;color:rgba(4,120,87,0.8);">/yr</span>
										</div>
										<div style="font-size:11px;color:#64748b;">Ongoing annual fee, years 2+</div>
									</div>
								</div>
								<div style="text-align:right;flex-shrink:0;">
									<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#64748b;margin-bottom:4px;">Adjustment</div>
									<div style="font-size:30px;font-weight:600;" :class="adjClass(pricingResult.adjustmentFactors.totalAdjustment)">
										{{ pricingResult.adjustmentFactors.totalAdjustment >= 0 ? '+' : '' }}{{ (pricingResult.adjustmentFactors.totalAdjustment * 100).toFixed(0) }}%
									</div>
									<div style="font-size:12px;color:#64748b;">distributed across setup + annual</div>
								</div>
							</div>
							<div style="font-size:12px;color:#64748b;margin-bottom:8px;">
								{{ pricingResult.recommendedBundleIds.length }} relevant bundle{{ pricingResult.recommendedBundleIds.length !== 1 ? 's' : '' }} for this population
							</div>
							<!-- Adjustment factor pills -->
							<div v-if="adjustmentPills.length" class="d-flex flex-wrap gap-2">
								<span
									v-for="f in adjustmentPills"
									:key="f.key"
									:class="f.direction === 'positive' ? 'cwpe-pill-pos' : f.direction === 'negative' ? 'cwpe-pill-neg' : 'cwpe-pill-neutral'"
									style="font-size:11px;font-weight:500;padding:2px 8px;border-radius:4px;"
								>
									{{ f.label }} {{ f.deltaPct >= 0 ? '+' : '' }}{{ (f.deltaPct * 100).toFixed(0) }}%
									<span class="ms-1 fw-normal opacity-75">({{ f.reason }})</span>
								</span>
							</div>
							<p v-if="pricingResult.missingInputs && pricingResult.missingInputs.length" class="mt-2 mb-0 small" style="color:#b45309;">
								Missing inputs: {{ pricingResult.missingInputs.join(', ') }}
							</p>
						</div>

					</div><!-- /col-lg-6 -->

					<!-- ── Right: meta sidebar ───────────────────────────────── -->
					<aside class="col-lg-3 border-start" style="background:#f8fafc;padding:24px;">

						<!-- Homepage -->
						<div class="mb-4">
							<div class="d-flex justify-content-between align-items-baseline mb-2">
								<div class="cwpe-label">Homepage</div>
								<button
									v-if="effectiveWebsite || rejectedUrls.length"
									type="button"
									@click="lookAgain"
									:disabled="lookingAgain"
									class="cwpe-look-again-btn"
									style="font-size:12px;color:#1d4ed8;background:none;border:none;cursor:pointer;padding:0;text-decoration:none;"
								>
									{{ lookingAgain ? 'Looking…' : rejectedUrls.length ? `Look again (${rejectedUrls.length} skipped)` : 'Look again' }}
								</button>
							</div>
							<a
								v-if="effectiveWebsite"
								:href="effectiveWebsite"
								target="_blank"
								rel="noreferrer"
								class="cwpe-website-link"
								style="font-size:14px;color:#1d4ed8;word-break:break-all;display:block;text-decoration:none;"
							>{{ effectiveWebsite }}</a>
							<p v-else style="font-size:14px;color:#94a3b8;font-style:italic;margin-bottom:0;">
								No website found (OSM, Wikidata, .gov/.us patterns, and mc2 database all empty).
							</p>
							<p v-if="effectiveWebsite && websiteSource" class="mb-0 mt-1" style="font-size:10px;color:#64748b;">
								source: <span style="color:#475569;">{{ websiteSource }}</span>
							</p>
							<p v-if="mc2MunicipalityWebsite" class="mb-0 mt-1" style="font-size:11px;color:#64748b;">
								mc2 record:
								<a :href="mc2MunicipalityWebsite" target="_blank" rel="noreferrer"
									style="color:#1d4ed8;text-decoration:none;word-break:break-all;">{{ mc2MunicipalityWebsite }}</a>
							</p>
							<p v-if="rejectedUrls.length" class="mt-1 mb-0" style="font-size:11px;color:#64748b;">
								Skipping: <code>{{ rejectedUrls.map(u => hostnameOf(u)).join(', ') }}</code>
							</p>
							<div class="mt-1" style="font-size:11px;font-family:monospace;color:#64748b;">
								slug: <span style="color:#334155;">{{ muniSlug }}</span>
							</div>

							<!-- Thumbnail / preview -->
							<button
								v-if="cwpeData.thumbnail_url"
								type="button"
								@click="lightboxOpen = true"
								class="d-block w-100 border rounded bg-white overflow-hidden mt-2 p-0 cwpe-thumb-btn"
								title="Click to preview homepage"
							>
								<img
									:src="cwpeData.thumbnail_url"
									:alt="`${muniDisplayName} homepage`"
									class="img-fluid"
									style="width:100%;height:auto;"
									loading="lazy"
								/>
							</button>

							<!-- Website exists, no thumbnail — favicon-based mini preview + actions -->
							<a
								v-else-if="effectiveWebsite"
								:href="effectiveWebsite"
								target="_blank" rel="noreferrer"
								class="d-block border rounded bg-white text-decoration-none mt-2 p-3"
								style="border-color:#bfdbfe!important;"
							>
								<div class="d-flex align-items-center gap-2">
									<img
										:src="`https://www.google.com/s2/favicons?domain=${hostnameOf(effectiveWebsite)}&sz=64`"
										:alt="`${hostnameOf(effectiveWebsite)} favicon`"
										style="width:32px;height:32px;border-radius:6px;background:#f1f5f9;"
										loading="lazy"
									/>
									<div class="flex-grow-1 min-w-0">
										<div class="fw-medium text-truncate" style="font-size:13px;color:#0f172a;">{{ hostnameOf(effectiveWebsite) }}</div>
										<div style="font-size:11px;color:#64748b;">Open homepage ↗</div>
									</div>
								</div>
								<div class="d-flex gap-1 mt-2">
									<button type="button" @click.prevent.stop="captureThumbnail"
										:disabled="capturingThumbnail"
										class="btn btn-sm flex-grow-1"
										style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:11px;padding:4px 8px;">
										<span v-if="capturingThumbnail" class="spinner-border spinner-border-sm me-1" style="width:10px;height:10px;border-width:1.5px;"></span>
										{{ capturingThumbnail ? 'Capturing…' : 'Capture preview' }}
									</button>
								</div>
								<p v-if="captureMsg" class="mb-0 mt-1" style="font-size:11px;" :style="{ color: captureMsgTone === 'ok' ? '#047857' : '#9f1239' }">{{ captureMsg }}</p>
							</a>

							<!-- No website at all — placeholder -->
							<div v-else
								class="border rounded d-flex flex-column align-items-center justify-content-center mt-2 p-3"
								style="height:160px;border-style:dashed!important;background:#fafbfc;"
							>
								<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
									<rect x="2" y="4" width="20" height="16" rx="2"/>
									<path d="M2 8h20"/><path d="M6 6h.01"/><path d="M9 6h.01"/>
								</svg>
								<div class="mt-2" style="font-size:12px;color:#94a3b8;text-align:center;">No website yet — preview will appear after one is captured.</div>
							</div>

							<!-- Current provider — shows ALL sources we have a hit on -->
							<div class="mt-3">
								<div class="d-flex align-items-baseline justify-content-between">
									<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;">Current provider</div>
									<span v-if="!providerSources.length"
										class="rounded-circle d-inline-flex align-items-center justify-content-center"
										style="width:16px;height:16px;background:#f1f5f9;color:#64748b;font-size:11px;cursor:help;"
										:title="providerSourcesExplain">ⓘ</span>
								</div>
								<div v-if="providerSources.length" class="small mt-1 d-flex flex-column gap-1">
									<div v-for="(p, i) in providerSources" :key="i" class="d-flex align-items-center gap-2">
										<span v-if="p.color" class="d-inline-block rounded-circle" :style="{ width:'8px', height:'8px', background: p.color }"></span>
										<span style="font-weight:500;color:#0f172a;">{{ p.name }}</span>
										<span v-if="p.kind === 'self'" class="rounded fw-semibold px-1" style="font-size:9px;background:#dbeafe;color:#1e3a8a;">us</span>
										<span v-else-if="p.kind === 'detected'" class="rounded fw-semibold px-1" style="font-size:9px;background:#dcfce7;color:#166534;">auto</span>
										<span v-else class="rounded fw-semibold px-1" style="font-size:9px;background:#f1f5f9;color:#475569;">manual</span>
										<span style="color:#94a3b8;font-size:10px;flex-grow:1;text-align:right;">{{ p.source }}</span>
									</div>
								</div>
								<div v-else style="color:#94a3b8;font-style:italic;font-size:12px;">
									unknown
									<div class="mt-1" style="font-size:10px;font-style:normal;color:#64748b;line-height:1.4;">
										No hit on <code>cwpe.current_provider</code>, <code>muni.competitor</code>, <code>muni.competitor_hg</code>, or platform auto-detection.
									</div>
								</div>
							</div>
						</div>

						<div style="border-top:1px solid #e2e8f0;padding-top:12px;font-size:12px;color:#64748b;">
							<span style="font-weight:500;color:#334155;">Lookup source:</span> {{ cwpeData.lookup_source || '—' }}
						</div>

						<!-- ─── Peer pricing (TW clients in same state / county) ─── -->
						<div style="border-top:1px solid #e2e8f0;margin-top:16px;padding-top:16px;">
							<h3 class="cwpe-label mb-2" style="font-size:11px;letter-spacing:0.05em;">Peer pricing</h3>

							<div v-if="peerPricingLoading" style="font-size:12px;color:#94a3b8;font-style:italic;">
								<div class="spinner-border spinner-border-sm me-2" role="status" style="width:12px;height:12px;border-width:1.5px;"></div>
								Loading peer pricing…
							</div>

							<template v-else-if="peerPricing">
								<!-- Average price stat cards: county + state -->
								<div class="row g-2 mb-3">
									<div class="col-6">
										<div class="rounded p-2 h-100" style="border:1px solid #e2e8f0;background:white;">
											<div class="text-uppercase fw-semibold mb-1" style="font-size:9px;letter-spacing:0.05em;color:#64748b;">County avg</div>
											<div class="fw-semibold font-monospace" style="font-size:15px;color:#0f172a;">
												{{ peerPricing.county_average_annual_cents != null ? cents(peerPricing.county_average_annual_cents) : '—' }}
											</div>
											<div style="font-size:10px;color:#94a3b8;">
												<template v-if="peerPricing.county_clients_count">
													{{ peerPricing.county_clients_count }} client{{ peerPricing.county_clients_count === 1 ? '' : 's' }}<span v-if="peerPricing.target.county"> · {{ peerPricing.target.county }}</span>
												</template>
												<template v-else>no priced clients</template>
											</div>
										</div>
									</div>
									<div class="col-6">
										<div class="rounded p-2 h-100" style="border:1px solid #e2e8f0;background:white;">
											<div class="text-uppercase fw-semibold mb-1" style="font-size:9px;letter-spacing:0.05em;color:#64748b;">State avg</div>
											<div class="fw-semibold font-monospace" style="font-size:15px;color:#0f172a;">
												{{ peerPricing.state_average_annual_cents != null ? cents(peerPricing.state_average_annual_cents) : '—' }}
											</div>
											<div style="font-size:10px;color:#94a3b8;">
												<template v-if="peerPricing.state_clients_count">
													{{ peerPricing.state_clients_count }} client{{ peerPricing.state_clients_count === 1 ? '' : 's' }} · {{ peerPricing.target.state }}
												</template>
												<template v-else>no priced clients</template>
											</div>
										</div>
									</div>
								</div>

								<!-- Closest peer in county (1 visible, show more up to 5) -->
								<div v-if="peerPricing.county_peers && peerPricing.county_peers.length" class="mb-3">
									<div class="d-flex align-items-baseline justify-content-between" style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;">
										<span>Closest in county</span>
										<span v-if="peerPricing.target.population" style="text-transform:none;color:#94a3b8;">target pop {{ Number(peerPricing.target.population).toLocaleString() }}</span>
									</div>
									<ul class="list-unstyled mb-0 mt-1">
										<li v-for="(p, idx) in (countyPeersExpanded ? peerPricing.county_peers : peerPricing.county_peers.slice(0, 1))"
											:key="`cp-${p.client_id}`"
											class="rounded mb-1 p-2" style="background:white;border:1px solid #e2e8f0;font-size:12px;">
											<div class="d-flex align-items-baseline justify-content-between gap-2">
												<a :href="`/clients/edit/${p.client_id}`" target="_blank" rel="noreferrer"
													class="fw-medium text-truncate text-decoration-none" style="color:#1d4ed8;flex:1;min-width:0;"
													:title="p.name">{{ p.name }}</a>
												<span v-if="idx === 0 && !countyPeersExpanded" class="rounded fw-semibold px-1 flex-shrink-0"
													:style="peerTooFar(p) ? 'font-size:9px;background:#fed7aa;color:#9a3412;' : 'font-size:9px;background:#dbeafe;color:#1e3a8a;'">{{ peerTooFar(p) ? 'stretch' : 'closest' }}</span>
											</div>
											<div class="d-flex flex-wrap gap-2 mt-1" style="font-size:11px;color:#475569;">
												<span>pop {{ Number(p.population).toLocaleString() }}<span v-if="p.pop_distance != null" style="color:#94a3b8;"> · Δ {{ Number(p.pop_distance).toLocaleString() }}</span></span>
												<span v-if="p.package" class="text-truncate" style="color:#64748b;">{{ p.package }}</span>
											</div>
											<div class="font-monospace fw-semibold mt-1" style="font-size:12px;color:#0f172a;">
												{{ p.annual_cents != null ? `${cents(p.annual_cents)}/yr` : 'no billing on file' }}
											</div>
										</li>
									</ul>
									<div v-if="peerTooFar(peerPricing.county_peers[0])" class="rounded mt-1 px-2 py-1"
										style="background:#fff7ed;border:1px solid #fed7aa;font-size:11px;color:#9a3412;line-height:1.4;">
										No close peer in {{ peerPricing.target.county || 'this county' }} — nearest is more than 2× off in population. Treat as a rough reference, not a price anchor.
									</div>
									<button v-if="peerPricing.county_peers.length > 1" type="button" @click="countyPeersExpanded = !countyPeersExpanded"
										class="btn btn-link btn-sm p-0" style="font-size:11px;color:#1d4ed8;text-decoration:none;">
										{{ countyPeersExpanded ? '▴ show closest only' : `▾ show ${peerPricing.county_peers.length - 1} more` }}
									</button>
								</div>
								<div v-else-if="peerPricing.target.county" class="mb-3" style="font-size:11px;color:#94a3b8;font-style:italic;">
									No TW clients in {{ peerPricing.target.county }}.
								</div>

								<!-- Closest peer in state (1 visible, show more up to 5) -->
								<div v-if="peerPricing.state_peers && peerPricing.state_peers.length" class="mb-2">
									<div class="d-flex align-items-baseline justify-content-between" style="font-size:10px;text-transform:uppercase;letter-spacing:0.025em;color:#94a3b8;">
										<span>Closest in state</span>
										<span v-if="peerPricing.state_total_clients" style="text-transform:none;color:#94a3b8;">{{ peerPricing.state_total_clients }} TW clients</span>
									</div>
									<ul class="list-unstyled mb-0 mt-1">
										<li v-for="(p, idx) in (statePeersExpanded ? peerPricing.state_peers : peerPricing.state_peers.slice(0, 1))"
											:key="`sp-${p.client_id}`"
											class="rounded mb-1 p-2" style="background:white;border:1px solid #e2e8f0;font-size:12px;">
											<div class="d-flex align-items-baseline justify-content-between gap-2">
												<a :href="`/clients/edit/${p.client_id}`" target="_blank" rel="noreferrer"
													class="fw-medium text-truncate text-decoration-none" style="color:#1d4ed8;flex:1;min-width:0;"
													:title="p.name">{{ p.name }}<span v-if="p.county" style="color:#94a3b8;font-weight:400;"> · {{ p.county }}</span></a>
												<span v-if="idx === 0 && !statePeersExpanded" class="rounded fw-semibold px-1 flex-shrink-0"
													:style="peerTooFar(p) ? 'font-size:9px;background:#fed7aa;color:#9a3412;' : 'font-size:9px;background:#dbeafe;color:#1e3a8a;'">{{ peerTooFar(p) ? 'stretch' : 'closest' }}</span>
											</div>
											<div class="d-flex flex-wrap gap-2 mt-1" style="font-size:11px;color:#475569;">
												<span>pop {{ Number(p.population).toLocaleString() }}<span v-if="p.pop_distance != null" style="color:#94a3b8;"> · Δ {{ Number(p.pop_distance).toLocaleString() }}</span></span>
												<span v-if="p.package" class="text-truncate" style="color:#64748b;">{{ p.package }}</span>
											</div>
											<div class="font-monospace fw-semibold mt-1" style="font-size:12px;color:#0f172a;">
												{{ p.annual_cents != null ? `${cents(p.annual_cents)}/yr` : 'no billing on file' }}
											</div>
										</li>
									</ul>
									<div v-if="peerTooFar(peerPricing.state_peers[0])" class="rounded mt-1 px-2 py-1"
										style="background:#fff7ed;border:1px solid #fed7aa;font-size:11px;color:#9a3412;line-height:1.4;">
										No close peer in {{ peerPricing.target.state }} — nearest is more than 2× off in population. Treat as a rough reference, not a price anchor.
									</div>
									<button v-if="peerPricing.state_peers.length > 1" type="button" @click="statePeersExpanded = !statePeersExpanded"
										class="btn btn-link btn-sm p-0" style="font-size:11px;color:#1d4ed8;text-decoration:none;">
										{{ statePeersExpanded ? '▴ show closest only' : `▾ show ${peerPricing.state_peers.length - 1} more` }}
									</button>
								</div>
							</template>
						</div>

					</aside>

				</div><!-- /row -->

				<!-- ── Bundle section ────────────────────────────────────────── -->
				<div style="border-top:1px solid #e2e8f0;padding:20px 24px;">

					<!-- Quote context bar -->
					<div style="border-radius:8px;border:1px solid #e2e8f0;background:white;margin-bottom:16px;">
						<div style="padding:12px 16px;">
							<div class="d-flex align-items-start gap-4 flex-wrap">

								<!-- Project type -->
								<div>
									<div class="cwpe-label mb-1">Project type</div>
									<div class="d-inline-flex border rounded overflow-hidden cwpe-btn-group">
										<button type="button" class="cwpe-ctx-btn" :class="mode === 'new' ? 'cwpe-ctx-btn-primary' : 'cwpe-ctx-btn-idle'" @click="setMode('new')">New</button>
										<button type="button" class="cwpe-ctx-btn border-start" :class="mode === 'upgrade' ? 'cwpe-ctx-btn-upgrade' : 'cwpe-ctx-btn-idle'" @click="setMode('upgrade')">Upgrade</button>
									</div>
									<div class="cwpe-sublabel mt-1">
										<span v-if="mode === 'new'">First-time Town Web customer</span>
										<span v-else>Existing customer · setup × 0.50</span>
									</div>
								</div>

								<!-- Contract term -->
								<div>
									<div class="cwpe-label mb-1">Contract term</div>
									<div class="d-inline-flex border rounded overflow-hidden cwpe-btn-group">
										<button
											v-for="y in [1,2,3,4,5]"
											:key="y"
											type="button"
											class="cwpe-ctx-btn"
											:class="[y > 1 ? 'border-start' : '', contractYears === y ? 'cwpe-ctx-btn-dark' : 'cwpe-ctx-btn-idle']"
											@click="setContractYears(y)"
										>{{ y }}y</button>
									</div>
									<div class="cwpe-sublabel mt-1">
										{{ contractYears === 1 ? 'No term discount' : 'Longer terms lock in better annual pricing' }}
									</div>
								</div>

								<!-- Upgrade-only: prior year annual + target increase -->
								<template v-if="mode === 'upgrade'">
									<div style="min-width:220px;max-width:340px;flex:1 1 220px;">
										<div class="cwpe-label cwpe-label-amber mb-1">Prior year annual</div>
										<div class="d-flex align-items-center gap-2">
											<div class="position-relative flex-grow-1">
												<span class="position-absolute top-50 translate-middle-y ps-2 text-secondary cwpe-dollar-sign">$</span>
												<input
													v-model.number="priorAnnualDollars"
													type="number"
													min="0"
													step="100"
													@change="reprice"
													placeholder="e.g. 2500"
													class="form-control form-control-sm cwpe-upgrade-input ps-4 font-monospace"
												/>
											</div>
											<button
												type="button"
												class="btn btn-sm cwpe-upload-btn"
												@click="pickInvoiceFile"
												:disabled="uploading"
											>{{ uploading ? 'Parsing…' : 'Upload invoice' }}</button>
											<button
												v-if="priorAnnualDollars != null"
												type="button"
												@click="clearPriorAnnual"
												class="btn btn-sm btn-link p-0 lh-1"
												style="color:#94a3b8;font-size:16px;line-height:1;"
												title="Clear anchor"
											>×</button>
										</div>
										<input ref="invoiceFileEl" type="file" accept="application/pdf" class="d-none" @change="onInvoiceFilePicked" />
										<p v-if="uploadError" class="mt-1 mb-0 text-danger" style="font-size:11px;">{{ uploadError }}</p>
										<p v-else-if="uploadInfo" class="mt-1 mb-0 text-success" style="font-size:11px;">{{ uploadInfo }}</p>
										<p v-else class="mt-1 mb-0 text-muted" style="font-size:11px;line-height:1.35;">
											Anchors the % increase calc. Drop in a prior invoice (PDF) to parse it automatically.
										</p>
									</div>

									<div v-if="priorAnnualDollars !== null && priorAnnualDollars !== ''">
										<div class="cwpe-label cwpe-label-amber mb-1">Target increase</div>
										<div class="d-flex align-items-center gap-2 mb-1">
											<input
												v-model.number="targetIncreasePct"
												@input="reprice"
												type="range"
												min="0"
												max="15"
												step="1"
												class="cwpe-range"
												style="width:128px;"
											/>
											<span class="cwpe-pct-badge">+{{ targetIncreasePct }}%</span>
										</div>
										<div class="cwpe-sublabel">
											Target ≈ {{ cents(Math.round((priorAnnualDollars || 0) * 100 * (1 + targetIncreasePct / 100))) }}/yr
										</div>
									</div>
								</template>

							</div>
						</div>
					</div>

					<!-- Bundle grid header -->
					<div class="d-flex align-items-baseline justify-content-between mb-3">
						<div>
							<div class="fw-semibold mb-1" style="font-size:14px;color:#0f172a;">Bundle quotes</div>
							<div style="font-size:12px;color:#64748b;">
								Base pricing from the 2025 Town Web bundle grid, adjusted for regional factors.
								Relevance filter highlights bundles matching this population.
								<span v-if="mode === 'upgrade'" class="ms-1" style="color:#92400e;font-weight:500;">
									Upgrade mode — setup discounted{{ contractYears > 1 ? `, ${contractYears}y term` : '' }}.
								</span>
							</div>
						</div>
						<div class="d-flex align-items-center gap-3" style="font-size:12px;">
							<span v-if="selectedBundleId" style="color:#1d4ed8;font-weight:500;">
								Selected: <span class="fw-semibold">{{ selectedBundleId.toUpperCase() }}</span> — carries over to
								<router-link to="/cwpe/quotes" style="color:#1d4ed8;text-decoration:underline;">Quote Builder</router-link>
								and <router-link to="/cwpe/pricing" style="color:#1d4ed8;text-decoration:underline;">Competitive comparison</router-link>
							</span>
							<span v-else class="fst-italic" style="color:#64748b;">Click a bundle to pre-fill the other pages</span>
							<router-link to="/cwpe/pricing" class="ms-auto" style="color:#1d4ed8;">Open pricing engine →</router-link>
						</div>
					</div>

					<!-- Bundle cards grid -->
					<div class="row g-3">
						<div class="col-12 col-md-6 col-xl-4" v-for="b in enrichedBundles" :key="b.bundleId">
							<button
								type="button"
								class="w-100 text-start rounded p-3 cwpe-bundle-card"
								:class="{
									'cwpe-bundle-selected': selectedBundleId === b.bundleId,
									'cwpe-bundle-recommended': b.recommended && selectedBundleId !== b.bundleId,
									'cwpe-bundle-default': !b.recommended && selectedBundleId !== b.bundleId,
								}"
								@click="pickBundle(b.bundleId)"
							>
								<div class="d-flex align-items-baseline justify-content-between mb-2">
									<div class="d-flex align-items-baseline gap-2">
										<span style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;">{{ b.bundleId.toUpperCase() }}</span>
										<span v-if="selectedBundleId === b.bundleId" style="background:#2563eb;color:white;padding:2px 6px;border-radius:9999px;font-size:9px;text-transform:uppercase;letter-spacing:0.025em;">Selected</span>
										<span v-else-if="b.recommended" style="background:#f59e0b;color:white;padding:2px 6px;border-radius:9999px;font-size:9px;text-transform:uppercase;letter-spacing:0.025em;">Fit</span>
									</div>
									<span style="font-size:12px;color:#94a3b8;">×{{ b.multiplier.toFixed(2) }}</span>
								</div>
								<div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.25;margin-bottom:4px;">{{ b.bundleName }}</div>
								<div style="font-size:11px;color:#64748b;margin-top:2px;margin-bottom:8px;line-height:1.375;">{{ bundleDesc(b.bundleId) }}</div>

								<div style="font-size:11px;font-family:monospace;padding-top:8px;border-top:1px solid rgba(226,232,240,0.7);">
									<!-- Setup -->
									<div class="d-flex justify-content-between py-1">
										<span style="color:#64748b;">
											Setup
											<span style="font-size:9px;color:#94a3b8;">one-time · incl. adj</span>
										</span>
										<span style="color:#1e293b;">{{ cents(b.setupCents) }}</span>
									</div>
									<!-- Annual -->
									<div class="d-flex justify-content-between py-1 rounded" style="background:rgba(236,253,245,0.5);margin:0 -4px;padding-left:4px;padding-right:4px;">
										<span style="color:#064e3b;font-weight:500;">
											Annual
											<span style="font-size:9px;color:rgba(4,120,87,0.8);">/yr · incl. adj</span>
										</span>
										<span style="color:#064e3b;font-weight:600;">{{ cents(b.annualCents) }}</span>
									</div>
									<!-- Adjustment note -->
									<div v-if="b.adjustmentPct !== 0" style="font-size:10px;color:#64748b;font-style:italic;" class="py-1">
										Above includes regional adjustment of
										<span :style="b.adjustmentPct > 0 ? 'color:#047857;' : 'color:#be123c;'">
											{{ b.adjustmentPct > 0 ? '+' : '' }}{{ (b.adjustmentPct * 100).toFixed(1) }}%
										</span>
									</div>
									<!-- First-year total -->
									<div class="d-flex justify-content-between py-1 border-top mt-1">
										<span style="font-weight:600;color:#334155;">First-year</span>
										<span style="font-weight:600;font-size:14px;color:#0f172a;">{{ cents(b.firstYearCents) }}</span>
									</div>
									<!-- Δ vs prior year (upgrade mode) -->
									<div
										v-if="mode === 'upgrade' && b.deltaVsPriorYearPct != null"
										class="d-flex justify-content-between align-items-baseline py-1 border-top"
										style="font-size:10px;"
									>
										<span style="color:#92400e;">Δ vs prior year</span>
										<span
											class="fw-semibold"
											:class="b.deltaVsPriorYearPct >= 0 ? 'cwpe-trendcolor-pos' : 'cwpe-trendcolor-neg'"
											style="font-family:ui-monospace,monospace;"
										>
											{{ b.deltaVsPriorYearPct >= 0 ? '+' : '' }}{{ (b.deltaVsPriorYearPct * 100).toFixed(1) }}%
											<span v-if="b.onTarget" style="background:#059669;color:white;padding:0 4px;border-radius:2px;font-size:9px;text-transform:uppercase;letter-spacing:0.025em;margin-left:6px;">On target</span>
										</span>
									</div>
								</div>

								<!-- Includes -->
								<div class="mt-2" style="font-size:10px;">
									<div style="font-weight:500;color:#475569;margin-bottom:2px;">Includes</div>
									<ul class="list-unstyled mb-0">
										<li v-for="(it, fi) in bundleIncludes(b.bundleId).slice(0, 4)" :key="fi" style="color:#64748b;margin-bottom:0;">· {{ it }}</li>
										<li v-if="bundleIncludes(b.bundleId).length > 4" style="color:#94a3b8;font-style:italic;">
											+ {{ bundleIncludes(b.bundleId).length - 4 }} more…
										</li>
									</ul>
								</div>
							</button>
						</div>
					</div>

				</div><!-- /bundle section -->

				<footer style="border-top:1px solid #e2e8f0;background:#f8fafc;padding:12px 24px;display:flex;justify-content:flex-end;font-size:12px;color:#64748b;">
					Last lookup: {{ formatDate(cwpeData.looked_up_at) }}
				</footer>
			</section>

	<!-- ── Lightbox ──────────────────────────────────────────────────── -->
	<div
		v-if="lightboxOpen && cwpeData && cwpeData.thumbnail_url"
		@click.self="lightboxOpen = false"
		style="position:fixed;inset:0;z-index:9000;background:rgba(2,6,23,0.8);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px;"
	>
		<div style="position:relative;max-width:1152px;max-height:100%;">
			<button
				type="button"
				@click="lightboxOpen = false"
				style="position:absolute;top:-12px;right:-12px;width:32px;height:32px;border-radius:50%;background:white;border:none;font-size:18px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.3);color:#334155;"
				aria-label="Close"
			>×</button>
			<img
				:src="cwpeData.thumbnail_url"
				:alt="`${muniDisplayName} homepage preview`"
				style="display:block;max-width:100%;max-height:90vh;border-radius:8px;box-shadow:0 20px 60px rgba(0,0,0,0.5);"
			/>
			<div class="text-center mt-2" style="font-size:12px;color:#cbd5e1;">
				<a v-if="cwpeData.website_url" :href="cwpeData.website_url" target="_blank" rel="noreferrer" style="color:#e2e8f0;text-decoration:none;">{{ cwpeData.website_url }}</a>
				<span style="color:#64748b;margin-left:8px;">· press Esc to close</span>
			</div>
		</div>
	</div>

	<!-- ── Census details modal ──────────────────────────────────────── -->
	<!-- Opens on double-click of the muni name in the results header. -->
	<div
		v-if="censusModalOpen && cwpeData"
		@click.self="censusModalOpen = false"
		style="position:fixed;inset:0;z-index:9000;background:rgba(2,6,23,0.6);backdrop-filter:blur(4px);display:flex;align-items:flex-start;justify-content:center;padding:48px 24px;overflow-y:auto;"
	>
		<div class="bg-white rounded shadow-lg" style="max-width:720px;width:100%;margin:auto;">
			<!-- Header -->
			<div class="d-flex align-items-start justify-content-between p-3" style="border-bottom:1px solid #e2e8f0;">
				<div>
					<div class="cwpe-label" style="font-size:10px;letter-spacing:0.05em;color:#1d4ed8;">Census details</div>
					<h2 class="mt-1 mb-0" style="font-size:18px;font-weight:600;color:#0f172a;">{{ muniDisplayName }}, {{ form.state }}</h2>
					<div v-if="cwpeData.county_name" style="font-size:12px;color:#64748b;margin-top:2px;">{{ cwpeData.county_name }}</div>
				</div>
				<button type="button" @click="censusModalOpen = false"
					class="btn btn-link p-0"
					style="color:#94a3b8;font-size:24px;line-height:1;text-decoration:none;"
					aria-label="Close">×</button>
			</div>

			<!-- Body -->
			<div class="p-3" style="font-size:13px;color:#334155;">
				<!-- Geo / FIPS -->
				<div class="mb-3">
					<div class="cwpe-label mb-2" style="font-size:10px;letter-spacing:0.05em;color:#475569;">Geo / FIPS</div>
					<div class="row g-2">
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">State FIPS</div>
							<div style="font-family:ui-monospace,monospace;font-weight:500;">{{ cwpeData.state_fips || '—' }}</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">County FIPS</div>
							<div style="font-family:ui-monospace,monospace;font-weight:500;">{{ cwpeData.county_fips || '—' }}</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Place FIPS</div>
							<div style="font-family:ui-monospace,monospace;font-weight:500;">{{ cwpeData.place_fips || '—' }}</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">County Subdivision FIPS</div>
							<div style="font-family:ui-monospace,monospace;font-weight:500;">{{ cwpeData.subdivision_fips || '—' }}</div>
						</div>
						<div class="col-12">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">County name</div>
							<div>{{ cwpeData.county_name || '—' }}</div>
						</div>
					</div>
				</div>

				<!-- Population -->
				<div class="mb-3 pt-3" style="border-top:1px solid #e2e8f0;">
					<div class="cwpe-label mb-2" style="font-size:10px;letter-spacing:0.05em;color:#475569;">Population (ACS 5-Year)</div>
					<div class="row g-2">
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Current ({{ cwpeData.population_year || '—' }})</div>
							<div style="font-family:ui-monospace,monospace;font-weight:600;font-size:15px;color:#0f172a;">
								{{ cwpeData.population != null ? Number(cwpeData.population).toLocaleString() : '—' }}
							</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Prior ({{ cwpeData.population_prior_year || '—' }})</div>
							<div style="font-family:ui-monospace,monospace;font-weight:600;font-size:15px;color:#0f172a;">
								{{ cwpeData.population_prior != null ? Number(cwpeData.population_prior).toLocaleString() : '—' }}
							</div>
						</div>
						<div class="col-12">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">5-yr trend</div>
							<div :style="'font-weight:600;font-family:ui-monospace,monospace;' + trendValueColor(cwpeData.population_trend_pct, 0)">
								{{ cwpeData.population_trend_pct != null ? fmtPct(cwpeData.population_trend_pct) : '—' }}
							</div>
						</div>
					</div>
				</div>

				<!-- SNAP -->
				<div class="mb-3 pt-3" style="border-top:1px solid #e2e8f0;">
					<div class="cwpe-label mb-2" style="font-size:10px;letter-spacing:0.05em;color:#475569;">SNAP (ACS S2201)</div>
					<div class="row g-2">
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Rate</div>
							<div style="font-family:ui-monospace,monospace;font-weight:600;">{{ cwpeData.snap_rate != null ? fmtPct(cwpeData.snap_rate) : '—' }}</div>
						</div>
						<div class="col-3">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Year</div>
							<div>{{ cwpeData.snap_year || '—' }}</div>
						</div>
						<div class="col-3">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">Level</div>
							<div class="text-capitalize">{{ cwpeData.snap_level || '—' }}</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">State SNAP rate</div>
							<div style="font-family:ui-monospace,monospace;">{{ cwpeData.state_snap_rate != null ? fmtPct(cwpeData.state_snap_rate) : '—' }}</div>
						</div>
					</div>
				</div>

				<!-- County / state trends -->
				<div class="mb-3 pt-3" style="border-top:1px solid #e2e8f0;">
					<div class="cwpe-label mb-2" style="font-size:10px;letter-spacing:0.05em;color:#475569;">Context trends</div>
					<div class="row g-2">
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">County 5-yr trend</div>
							<div style="font-family:ui-monospace,monospace;">{{ cwpeData.county_trend_pct != null ? fmtPct(cwpeData.county_trend_pct) : '—' }}</div>
						</div>
						<div class="col-6">
							<div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">State 5-yr trend</div>
							<div style="font-family:ui-monospace,monospace;">{{ cwpeData.state_trend_pct != null ? fmtPct(cwpeData.state_trend_pct) : '—' }}</div>
						</div>
						<div v-if="cwpeData.context_data && cwpeData.context_data.county" class="col-12 mt-1" style="font-size:11px;color:#64748b;">
							County pop: {{ cwpeData.context_data.county.population != null ? Number(cwpeData.context_data.county.population).toLocaleString() : '—' }}
							<span v-if="cwpeData.context_data.county.populationPrior"> · prior: {{ Number(cwpeData.context_data.county.populationPrior).toLocaleString() }}</span>
						</div>
						<div v-if="cwpeData.context_data && cwpeData.context_data.state" class="col-12" style="font-size:11px;color:#64748b;">
							State pop: {{ cwpeData.context_data.state.population != null ? Number(cwpeData.context_data.state.population).toLocaleString() : '—' }}
							<span v-if="cwpeData.context_data.state.populationPrior"> · prior: {{ Number(cwpeData.context_data.state.populationPrior).toLocaleString() }}</span>
						</div>
					</div>
				</div>

				<!-- Website (Census-resolved) -->
				<div v-if="cwpeData.website_url" class="mb-3 pt-3" style="border-top:1px solid #e2e8f0;">
					<div class="cwpe-label mb-2" style="font-size:10px;letter-spacing:0.05em;color:#475569;">Website (Census)</div>
					<a :href="cwpeData.website_url" target="_blank" rel="noreferrer"
						style="color:#1d4ed8;text-decoration:none;word-break:break-all;font-size:13px;">{{ cwpeData.website_url }}</a>
				</div>

				<!-- Meta -->
				<div class="pt-3" style="border-top:1px solid #e2e8f0;font-size:11px;color:#64748b;">
					<div><span style="font-weight:500;color:#475569;">Lookup source:</span> {{ cwpeData.lookup_source || '—' }}</div>
					<div v-if="cwpeData.looked_up_at"><span style="font-weight:500;color:#475569;">Looked up:</span> {{ fmtCompactDate(cwpeData.looked_up_at) }}</div>
				</div>
			</div>
		</div>
	</div>
	</div><!-- /container-xl -->
	</div><!-- /root -->
</template>

<script>
import { mapState } from 'vuex'
import restApi from '../../api/index.js'

const US_STATES = [
	'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
	'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
	'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
	'VA','WA','WV','WI','WY','DC',
]

const RECENT_KEY  = 'cwpe_recent_munis'
const ACTIVE_KEY  = 'cwpe_active_muni'
const QUOTE_CTX_KEY = 'cwpe_quote_context'
const BUNDLE_KEY  = 'cwpe_selected_bundle'

const BUNDLE_MULTIPLIERS = { b1: 1.00, b2: 1.30, b3: 1.95, b4: 2.925, b5: 4.8555 }

const BUNDLE_DESC = {
	b1: 'Base web + forms + alerts. Fits small communities.',
	b2: 'Adds payment-enabled forms, reservations, 311, basic CRM.',
	b3: 'Advanced CRM, Command Center, ClerkMinutes AI, ADA Auditor.',
	b4: 'Full Command Center, AI website search, hyperlocal newsletter.',
	b5: '24/7 Virtual Clerk, Municipal AI Assistant, operational analytics.',
}

const BUNDLE_INCLUDES = {
	b1: ['Standard Web Design','Hosting & Domain Name','Standard Support (Email/Ticket-based)','Subscription Management','Emergency Alerts (Basic)','HeyGov 311 Portal'],
	b2: ['Standard Web Design','Hosting & Domain Name','Standard Support (Email/Ticket-based)','Subscription Management','Online Forms (up to 10)','Online Payments (HeyGov Pay)','Reservations (up to 3 venues)','Emergency Alerts (Basic)','HeyGov 311 Portal','Domain Name Management'],
	b3: ['Everything in Bundle 2 (at higher limits)','Digital Forms (up to 20)','Reservations (up to 10 venues)','Standard Support','Advanced Citizens CRM (smart lists, geo-fencing)','Emergency Alerts — Premium','ClerkMinutes AI (up to 3 boards)'],
	b4: ['Everything in Bundle 3 (at higher limits)','Digital Forms (up to 150 items)','Reservations (up to 50 venues)','Municipal Center — all departments','Advanced automation & workflows','AI website search','Hyperlocal newsletter'],
	b5: ['Everything in Bundle 4 (unlimited forms)','Reservations (up to 50 venues)','ClerkMinutes (25 boards, 25 meetings/mo)','Municipal Help Desk (24/7 Virtual Clerk)','Municipal AI Assistant','Operational analytics'],
}

const FACTOR_REASON = {
	'growth-leader':  'outgrowing county and state',
	'growth-laggard': 'below regional growth',
	'snap-high':      'above 12% threshold',
	'snap-low':       'below 8% threshold',
	'state-tier':     'state adjustment',
	'pop-decline':    'population declining',
	'pop-growth':     'population growing',
}

const TIER_NAMES = { b1: 'entry', b2: 'value', b3: 'standard', b4: 'premium', b5: 'enterprise' }

export default {
	name: 'CwpeLookup',

	data() {
		return {
			US_STATES,

			// Dropbox app URL — same env var used in client/ClientEdit.vue
			dropboxAppUrl: process.env.VUE_APP_DROPBOX_URL || '',

			// Form state
			form: { name: '', state: 'WI', url: '' },

			// Municipality autocomplete
			suggestions: [],
			searching: false,
			showDropdown: false,
			activeIdx: -1,
			selectedMuniId: null,
			selectedMunicipality: null,

			// Lookup state
			loading: false,
			lookupError: null,
			lookingAgain: false,
			rejectedUrls: [],

			// Results
			cwpeData: null,
			pricingResult: null,

			// Internal mc2 data (clients, contacts, billing, AR, ADA, etc.)
			internalData: null,
			internalDataLoading: false,
			contactsExpanded: false,
			departmentsExpanded: false,
			notesExpanded: false,
			campaignsExpanded: false,
			capturingThumbnail: false,
			captureMsg: null,
			captureMsgTone: 'ok',

			// Peer pricing (TW clients in same state / county)
			peerPricing: null,
			peerPricingLoading: false,
			statePeersExpanded: false,
			countyPeersExpanded: false,

			// Quote context
			mode: 'new',
			contractYears: 1,
			priorAnnualDollars: null,
			targetIncreasePct: 0,

			// Invoice upload
			uploading: false,
			uploadError: null,
			uploadInfo: null,

			// Bundle selection
			selectedBundleId: null,

			// Lightbox
			lightboxOpen: false,

			// Census details modal — opened by double-clicking the muni name
			censusModalOpen: false,

			// Recent lookups
			recent: JSON.parse(localStorage.getItem(RECENT_KEY)) || [],
		}
	},

	created() {
		// Restore quote context (mode / term / prior year / target) from last session
		try {
			const ctx = JSON.parse(localStorage.getItem(QUOTE_CTX_KEY) || 'null')
			if (ctx) {
				if (ctx.mode)               this.mode               = ctx.mode
				if (ctx.contractYears)      this.contractYears      = ctx.contractYears
				if (ctx.priorAnnualDollars !== undefined) this.priorAnnualDollars = ctx.priorAnnualDollars
				if (ctx.targetIncreasePct !== undefined)  this.targetIncreasePct  = ctx.targetIncreasePct
			}
		} catch (_) { /* ignore */ }

		// Restore cross-page bundle selection
		try {
			const saved = localStorage.getItem(BUNDLE_KEY)
			if (saved) this.selectedBundleId = saved
		} catch (_) { /* ignore */ }

		// Restore active municipality from last session
		const saved = localStorage.getItem(ACTIVE_KEY)
		if (saved) {
			try {
				const muni = JSON.parse(saved)
				this.selectedMunicipality = muni
				this.selectedMuniId = muni.id
				this.form.name = muni.full_name || muni.name || ''
				this.form.state = muni.state || 'WI'
				this.loadCwpeData(muni)
			} catch (e) {
				localStorage.removeItem(ACTIVE_KEY)
			}
		}
	},

	mounted() {
		document.addEventListener('keydown', this.onDocKey)
		window.addEventListener('cwpe:active-muni-changed', this.onActiveMuniChanged)
		window.addEventListener('cwpe:muni-refreshing', this.onMuniRefreshing)
		window.addEventListener('cwpe:muni-refreshed', this.onMuniRefreshed)
	},

	beforeDestroy() {
		document.removeEventListener('keydown', this.onDocKey)
		window.removeEventListener('cwpe:active-muni-changed', this.onActiveMuniChanged)
		window.removeEventListener('cwpe:muni-refreshing', this.onMuniRefreshing)
		window.removeEventListener('cwpe:muni-refreshed', this.onMuniRefreshed)
	},

	computed: {
		...mapState(['authUser']),

		// Effective website: external lookup wins, else mc2 client website,
		// else mc2 muni website / secondary. Sales rep cares which one
		// surfaced — `websiteSource` records that.
		effectiveWebsite() {
			if (this.cwpeData && this.cwpeData.website_url) return this.cwpeData.website_url
			const c = this.internalData && this.internalData.client
			if (c && c.website) return this.normalizeUrl(c.website)
			const m = this.internalData && this.internalData.municipality
			if (m && m.website) return this.normalizeUrl(m.website)
			if (m && m.website_secondary) return this.normalizeUrl(m.website_secondary)
			return null
		},
		websiteSource() {
			if (!this.effectiveWebsite) return null
			if (this.cwpeData && this.cwpeData.website_url === this.effectiveWebsite) return null
			const c = this.internalData && this.internalData.client
			if (c && c.website && this.normalizeUrl(c.website) === this.effectiveWebsite) return 'mc2 client record'
			return 'mc2 municipality record'
		},
		// Secondary display: when Census resolved a different website than what
		// our mc2 municipalities row has on file, show both — Census on top
		// (primary, used for screenshot/lookup), mc2 record below for context.
		// Null when there's no mc2 record website, or it matches the primary.
		mc2MunicipalityWebsite() {
			const m = this.internalData && this.internalData.municipality
			if (!m) return null
			const url = m.website
				? this.normalizeUrl(m.website)
				: (m.website_secondary ? this.normalizeUrl(m.website_secondary) : null)
			if (!url) return null
			if (url === this.effectiveWebsite) return null
			return url
		},
		// Provider — collect every source we have data on so the rep can see
		// the full picture. WE come first when the muni is our customer:
		// "competitor" fields are about *other* vendors, but if they're a TW
		// or HG client today, the actual provider is us.
		providerSources() {
			const sources = []
			const c = this.internalData && this.internalData.client
			if (c && c.tw_client) sources.push({ name: 'Town Web', source: 'mc2 active client (TW)', kind: 'self' })
			if (c && c.hg_client) sources.push({ name: 'HeyGov', source: 'mc2 active client (HG)', kind: 'self' })

			const cw = this.cwpeData
			if (cw && cw.current_provider) {
				sources.push({ name: cw.current_provider, source: cw.current_provider_source || 'cwpe lookup', kind: 'manual' })
			}
			const m = this.internalData && this.internalData.municipality
			if (m && m.competitor) sources.push({ name: m.competitor, source: 'mc2 municipalities.competitor', kind: 'manual' })
			if (m && m.competitor_hg) sources.push({ name: m.competitor_hg, source: 'mc2 municipalities.competitor_hg (HeyGov)', kind: 'manual' })
			const fp = (this.internalData && this.internalData.vendor_matches) || []
			fp.forEach(f => {
				sources.push({ name: this.canonicalVendorName(f.name), source: `auto-detected${f.snapshot_date ? ` · ${this.fmtCompactDate(f.snapshot_date)}` : ''}`, kind: 'detected', color: f.color, type: f.type })
			})
			// De-dupe by name (case-insensitive). Keep first occurrence (self/manual wins over auto).
			const seen = new Set()
			return sources.filter(s => {
				s.name = this.canonicalVendorName(s.name)
				const key = String(s.name || '').toLowerCase().trim()
				if (!key || seen.has(key)) return false
				seen.add(key)
				return true
			})
		},
		effectiveProvider() {
			return this.providerSources[0] || null
		},
		providerSourcesExplain() {
			return [
				'Provider is checked from these mc2 sources, in order:',
				'',
				'1. cwpe_municipality_data.current_provider (manual)',
				'2. municipalities.competitor (manual)',
				'3. municipalities.competitor_hg (HeyGov, manual)',
				'4. auto-detected platform matches (scanner)',
				'',
				'If all four are empty, we show unknown. Auto-detection runs separately — if a website was just added, run a scan or fill the competitor field by hand.',
			].join('\n')
		},
		visibleContacts() {
			const list = this.internalData?.contacts || []
			return this.contactsExpanded ? list : []
		},
		campaignsCount() {
			return ((this.internalData && this.internalData.engagement && this.internalData.engagement.recent_campaigns) || []).length
		},
		visibleDepartments() {
			const list = this.internalData?.departments || []
			return this.departmentsExpanded ? list : list.slice(0, 3)
		},
		visibleNotes() {
			const list = this.internalData?.internal_notes || []
			return this.notesExpanded ? list : list.slice(0, 2)
		},
		activeAddons() {
			const a = this.internalData?.addons
			if (!a) return []
			const labels = {
				online_payments: 'Online Payments',
				branded_emails: 'Branded Emails',
				live_stream: 'Live Stream',
				facebook_syndication: 'FB Syndication',
				ada_premium: 'ADA Premium',
				white_glove_service: 'White Glove',
				tw_weather: 'TW Weather',
				heygov_311: 'HG 311',
				heygov_jurisdiction: 'HG Jurisdiction',
				heygov_pay: 'HG Pay',
				heygov_reserve: 'HG Reserve',
				heygov_license: 'HG License',
				heygov_clerk_minutes: 'ClerkMinutes',
			}
			return Object.keys(labels).filter(k => a[k]).map(k => labels[k])
		},
		vendorList() {
			const list = this.internalData?.vendor_matches || []
			const seen = new Set()
			const out = []
			for (const v of list) {
				const name = this.canonicalVendorName(v.name)
				const key = String(name || '').toLowerCase().trim()
				if (!key || seen.has(key)) continue
				seen.add(key)
				out.push({ ...v, name })
			}
			return out
		},
		techStack() {
			const m = this.internalData?.municipality
			if (!m) return []
			const all = [
				...(m.cms || []),
				...(m.technologies || []),
				...(m.providers || []),
				...(m.servers || []),
			].filter(Boolean)
			return [...new Set(all)].slice(0, 12)
		},
		hasAddressBlock() {
			const m = this.internalData?.municipality
			if (!m) return false
			return !!(m.address || m.zip || m.phone || m.email)
		},
		hasEngagement() {
			const e = this.internalData?.engagement
			if (!e) return false
			return !!(e.last_email_date || e.last_mail_date || e.last_delivery || e.active_sequence) || this.internalData?.subscribers_count != null
		},
		hasCrmLinks() {
			const c = this.internalData?.client
			return c && (c.salesforce_account_id || c.freshbooks_client_id || c.juniper_id || c.dropbox_id)
		},
		renewalCountdown() {
			const ends = this.internalData?.client?.contract_ends_at
			if (!ends) return null
			const dt = new Date(ends)
			const days = Math.round((dt.getTime() - Date.now()) / 86400000)
			if (days < 0) return `expired ${Math.abs(days)}d ago`
			if (days <= 90) return `renewal in ${days}d`
			return null
		},
		renewalChipStyle() {
			const ends = this.internalData?.client?.contract_ends_at
			if (!ends) return {}
			const dt = new Date(ends)
			const days = Math.round((dt.getTime() - Date.now()) / 86400000)
			if (days < 0) return { background:'#fee2e2', color:'#991b1b' }
			if (days <= 30) return { background:'#fed7aa', color:'#9a3412' }
			return { background:'#fef3c7', color:'#92400e' }
		},
		muniDisplayName() {
			return this.selectedMunicipality
				? (this.selectedMunicipality.full_name || this.selectedMunicipality.name || '')
				: this.form.name
		},

		muniSlug() {
			const name = this.muniDisplayName
			const state = this.form.state
			if (!name || !state) return ''
			return name
				.replace(/^(town of|township of|city of|village of|borough of)\s+/i, '')
				.replace(/[^A-Za-z0-9]+/g, '-')
				.replace(/^-|-$/g, '') + '-' + state.toUpperCase()
		},

		// FIPS we display is the canonical municipalities.fips (or fips_full
		// fallback). Census-derived place/subdivision FIPS columns on
		// cwpe_municipality_data stay for internal pricing/geo lookups but no
		// longer drive the UI.
		displayFips() {
			const m = this.internalData?.municipality
			return m?.fips || m?.fips_full || null
		},

		fipsBreakdown() {
			const m = this.internalData?.municipality
			if (!m) return []
			const out = []
			if (m.fips_full)   out.push({ label: 'Full FIPS', value: m.fips_full })
			if (m.state_fips)  out.push({ label: 'State',     value: m.state_fips })
			if (m.county_fips) out.push({ label: 'County',    value: m.county_fips })
			if (m.place_fips)  out.push({ label: 'Place',     value: m.place_fips })
			return out
		},

		// Display fallbacks — when Census/Nominatim came back empty (small
		// muni, lookup failure), use whatever mc2's own tables already know.
		muniPopDisplay() {
			if (this.cwpeData?.population != null) return this.cwpeData.population
			return this.internalData?.municipality?.population ?? null
		},
		countyPop() {
			if (this.cwpeData?.context_data?.county?.population != null) {
				return this.cwpeData.context_data.county.population
			}
			return this.internalData?.county?.population ?? null
		},
		countyTrend() {
			return this.cwpeData?.context_data?.county?.populationTrendPct != null
				? parseFloat(this.cwpeData.context_data.county.populationTrendPct)
				: null
		},
		statePop() {
			return this.cwpeData?.context_data?.state?.population ?? null
		},
		countyDisplay() {
			if (this.cwpeData?.county_name) return this.cwpeData.county_name
			if (this.internalData?.county?.name) return this.internalData.county.name
			if (this.internalData?.municipality?.county) return this.internalData.municipality.county
			return null
		},
		stateTrend() {
			return this.cwpeData?.context_data?.state?.populationTrendPct != null
				? parseFloat(this.cwpeData.context_data.state.populationTrendPct)
				: null
		},

		snapColor() {
			const r = this.cwpeData?.snap_rate
			if (r == null) return 'text-muted'
			if (r >= 0.12) return 'cwpe-snap-high'
			if (r <= 0.08) return 'cwpe-snap-low'
			return 'cwpe-snap-mid'
		},

		sparklinePath() {
			const pop = this.cwpeData?.population
			const popPrior = this.cwpeData?.population_prior
			const popYear = this.cwpeData?.population_year || 2023
			const popPriorYear = this.cwpeData?.population_prior_year || 2018
			if (!pop) return null

			let series = []
			if (popPrior && popPriorYear) {
				const years = popYear - popPriorYear
				if (years > 0) {
					for (let y = popPriorYear; y <= popYear; y++) {
						const t = (y - popPriorYear) / years
						series.push({ year: y, population: Math.round(popPrior + (pop - popPrior) * t) })
					}
				}
			}
			if (series.length < 2 && this.cwpeData?.population_trend_pct != null) {
				const trend = parseFloat(this.cwpeData.population_trend_pct)
				const annual = trend / 5
				for (let i = 5; i >= 0; i--) {
					series.push({ year: popYear - i, population: Math.round(pop / Math.pow(1 + annual, i)) })
				}
			}
			if (series.length < 2) return null

			const W = 120, H = 28, pad = 2
			const xs = series.map(p => p.year)
			const ys = series.map(p => p.population)
			const xMin = xs[0], xMax = xs[xs.length - 1]
			const yMin = Math.min(...ys), yMax = Math.max(...ys)
			const xRange = xMax - xMin || 1
			const yRange = yMax - yMin || 1
			return series.map((p, i) => {
				const x = (pad + ((p.year - xMin) / xRange) * (W - pad * 2)).toFixed(1)
				const y = (H - pad - ((p.population - yMin) / yRange) * (H - pad * 2)).toFixed(1)
				return `${i === 0 ? 'M' : 'L'}${x},${y}`
			}).join(' ')
		},

		enrichedBundles() {
			if (!this.pricingResult) return []
			const recIds = this.pricingResult.recommendedBundleIds || []
			return this.pricingResult.bundles.map(b => ({
				...b,
				recommended: recIds.includes(b.bundleId),
				multiplier: BUNDLE_MULTIPLIERS[b.bundleId] || 1,
			}))
		},

		recommendedBundles() {
			return this.enrichedBundles.filter(b => b.recommended)
		},

		priceRangeMin() {
			const pool = this.recommendedBundles.length ? this.recommendedBundles : this.enrichedBundles
			return pool.length ? Math.min(...pool.map(b => b.firstYearCents)) : null
		},
		priceRangeMax() {
			const pool = this.recommendedBundles.length ? this.recommendedBundles : this.enrichedBundles
			return pool.length ? Math.max(...pool.map(b => b.firstYearCents)) : null
		},
		annualRangeMin() {
			const pool = this.recommendedBundles.length ? this.recommendedBundles : this.enrichedBundles
			return pool.length ? Math.min(...pool.map(b => b.annualCents)) : null
		},
		annualRangeMax() {
			const pool = this.recommendedBundles.length ? this.recommendedBundles : this.enrichedBundles
			return pool.length ? Math.max(...pool.map(b => b.annualCents)) : null
		},

		adjustmentPills() {
			const skip = ['upgrade-setup', 'contract-term']
			const factors = this.pricingResult?.adjustmentFactors?.factors || []
			return factors
				.filter(f => f.deltaPct !== 0 && !skip.includes(f.key))
				.map(f => ({
					...f,
					direction: f.deltaPct > 0 ? 'positive' : f.deltaPct < 0 ? 'negative' : 'neutral',
					reason: FACTOR_REASON[f.key] || f.label,
				}))
		},

		tierLabel() {
			const id = this.pricingResult?.recommendedBundleIds?.[0]
			return TIER_NAMES[id] || 'standard'
		},

		tierBadgeClass() {
			const t = this.tierLabel
			const map = {
				entry:      'cwpe-tier-entry',
				value:      'cwpe-tier-value',
				standard:   'cwpe-tier-standard',
				premium:    'cwpe-tier-premium',
				enterprise: 'cwpe-tier-enterprise',
			}
			return map[t] || 'cwpe-tier-entry'
		},
	},

	methods: {
		// Vision Internet and GovAccess are both Granicus brands — collapse them
		// so the rep sees a single canonical provider name instead of three rows.
		canonicalVendorName(name) {
			if (!name) return name
			const n = String(name).trim()
			if (/^vision\s*internet$/i.test(n)) return 'Granicus'
			if (/^govaccess$/i.test(n)) return 'Granicus'
			return n
		},
		// ── Municipality autocomplete ─────────────────────────────────
		// Hits mc2's /municipalities/search nation-wide; the rep can type a
		// trailing ",ST" inline to narrow if they want.
		onMuniInput() {
			if (this._muniTimer) clearTimeout(this._muniTimer)
			const q = this.form.name.trim()
			if (q.length < 2) {
				this.suggestions = []
				this.showDropdown = false
				return
			}
			this.showDropdown = true
			this.searching = true
			this._muniTimer = setTimeout(() => {
				restApi.get('/municipalities/search', { params: { search: q, limit: 15 } })
					.then(({ data }) => {
						this.suggestions = Array.isArray(data) ? data : (data?.results || data?.rows || [])
						this.activeIdx = -1
						this.searching = false
					})
					.catch(() => {
						this.suggestions = []
						this.searching = false
					})
			}, 300)
		},

		onMuniFocus() {
			if (this.form.name.trim().length >= 3 && this.suggestions.length) {
				this.showDropdown = true
			}
		},

		onMuniBlur() {
			setTimeout(() => { this.showDropdown = false }, 200)
		},

		onMuniKeydown(e) {
			if (!this.showDropdown) return
			if (e.key === 'ArrowDown') {
				e.preventDefault()
				this.activeIdx = Math.min(this.activeIdx + 1, this.suggestions.length - 1)
			} else if (e.key === 'ArrowUp') {
				e.preventDefault()
				this.activeIdx = Math.max(this.activeIdx - 1, 0)
			} else if (e.key === 'Enter' && this.activeIdx >= 0) {
				e.preventDefault()
				this.selectSuggestion(this.suggestions[this.activeIdx])
			} else if (e.key === 'Escape') {
				this.showDropdown = false
			}
		},

		onDocKey(e) {
			if (e.key === 'Escape') {
				if (this.showDropdown) this.showDropdown = false
				if (this.lightboxOpen) this.lightboxOpen = false
				if (this.censusModalOpen) this.censusModalOpen = false
			}
		},

		selectSuggestion(s) {
			this.form.name = s.full_name || s.name
			this.form.state = s.state
			this.form.url = s.website || ''
			this.selectedMunicipality = s
			this.selectedMuniId = s.id
			this.suggestions = []
			this.showDropdown = false
			// Push the picked muni to the parent chip immediately, so the
			// header reflects the selection before the (potentially slow)
			// Census + internal-data + peer-pricing lookups complete.
			this.persistActiveMuni({
				id: s.id,
				full_name: s.full_name || s.name,
				name: s.full_name || s.name,
				state: s.state,
				county: s.county || null,
				website: s.website || null,
				population: s.population || null,
			})
			this.loadCwpeData(s)
		},

		persistActiveMuni(muniObj) {
			localStorage.setItem(ACTIVE_KEY, JSON.stringify(muniObj))
			window.dispatchEvent(new CustomEvent('cwpe:active-muni-changed'))
		},

		// External mutation of cwpe_active_muni (parent's Clear button, or a
		// future picker elsewhere) → re-sync the form. No-op when the event
		// is one we just dispatched ourselves (id matches what's already
		// selected).
		onActiveMuniChanged() {
			const raw = localStorage.getItem(ACTIVE_KEY)
			if (!raw) {
				if (!this.selectedMuniId) return  // already clear
				this.selectedMunicipality = null
				this.selectedMuniId = null
				this.form.name = ''
				this.form.url = ''
				this.cwpeData = null
				this.pricingResult = null
				this.internalData = null
				this.peerPricing = null
				return
			}
			let muni
			try { muni = JSON.parse(raw) } catch (e) { return }
			if (this.selectedMuniId === muni.id) return  // already in sync
			this.selectedMunicipality = muni
			this.selectedMuniId = muni.id
			this.form.name = muni.full_name || muni.name || ''
			if (muni.state) this.form.state = muni.state
			this.loadCwpeData(muni)
		},

		// Parent's "Refresh" button started — drop the loaded results so the
		// skeleton card (the "bars" loading state) shows immediately, for the
		// whole duration of the force-refresh, not just a flash at the end.
		onMuniRefreshing() {
			if (this.selectedMunicipality) this.cwpeData = null
		},

		// Force-refresh finished — reload the currently-selected muni so the
		// panel (and screenshot) reflect the now-fresh cache.
		onMuniRefreshed() {
			if (this.selectedMunicipality) this.loadCwpeData(this.selectedMunicipality)
		},

		// ── Lookup ────────────────────────────────────────────────────
		doLookup() {
			if (!this.selectedMunicipality) return
			this.loadCwpeData(this.selectedMunicipality)
		},

		loadCwpeData(muni) {
			this.loading = true
			this.lookupError = null
			// Wipe previous results immediately so the panel doesn't keep
			// showing the *previous* muni's website / vendor / contacts while
			// Census + internal-data calls are still in flight.
			this.cwpeData = null
			this.pricingResult = null
			this.internalData = null
			this.internalDataLoading = true
			this.peerPricing = null
			this.peerPricingLoading = true
			this.statePeersExpanded = false
			this.countyPeersExpanded = false

			// Persist active municipality + notify the header chip in CwpeParent.
			const muniObj = {
				id: muni.id,
				full_name: muni.full_name || muni.name,
				name: muni.full_name || muni.name,
				state: this.form.state || muni.state,
				county: muni.county || (this.cwpeData && this.cwpeData.county_name) || null,
				website: muni.website || null,
			}
			this.persistActiveMuni(muniObj)

			const payload = {
				municipality_id: muni.id,
				name: muni.full_name || muni.name || '',
				state: this.form.state || muni.state,
				mode: this.mode,
				contract_years: this.contractYears,
			}
			// Pass coords from mc2 municipalities row when available — saves
			// the backend a Nominatim hit and lets it skip flaky name-only
			// matches (e.g. "Township Of Wheeling" never resolves on OSM).
			const lat = parseFloat(muni.lat)
			const lon = parseFloat(muni.lng != null ? muni.lng : muni.lon)
			if (Number.isFinite(lat) && Number.isFinite(lon)) {
				payload.coords = { lat, lon }
			}
			if (this.mode === 'upgrade' && this.priorAnnualDollars) {
				payload.prior_year_annual_cents = Math.round(this.priorAnnualDollars * 100)
			}
			if (this.mode === 'upgrade' && this.targetIncreasePct) {
				payload.target_increase_pct = this.targetIncreasePct / 100
			}

			// Two parallel calls:
			//  - /cwpe/pricing/lookup → Census/SNAP/trend + pricing (existing flow)
			//  - /cwpe/lookup/internal-data → mc2 client/contacts/billing/AR/etc.
			const pricingPromise = restApi.post('/cwpe/pricing/lookup', payload)
				.then(({ data }) => {
					this.cwpeData = data.municipality_data
					this.pricingResult = data.pricing
					// Auto-select the right bundle for THIS muni's population.
					// If the rep had previously picked a bundle for a bigger
					// city (e.g. b5 for 100k pop) and now looks up a 300-pop
					// village, b5 is no longer in the recommended set —
					// override it. We only keep a stored choice when it's
					// still a fit for the new population band.
					const recommendedList = (data.pricing && data.pricing.recommendedBundleIds) || []
					const savedBundle = localStorage.getItem(BUNDLE_KEY)
					if (savedBundle && recommendedList.includes(savedBundle)) {
						// Stored pick still appropriate — honor it.
						this.selectedBundleId = savedBundle
					} else if (recommendedList.length) {
						// No stored pick, or stored pick is out-of-band for
						// this population — fall back to the first
						// recommendation and update storage to match.
						this.selectedBundleId = recommendedList[0]
						try { localStorage.setItem(BUNDLE_KEY, recommendedList[0]) } catch (_) { /* ignore */ }
					}
					this.rejectedUrls = []

					if (this.cwpeData?.prior_year_annual_cents && this.priorAnnualDollars === null) {
						this.priorAnnualDollars = this.cwpeData.prior_year_annual_cents / 100
						if (this.cwpeData.prior_year_contract_years) {
							this.contractYears = this.cwpeData.prior_year_contract_years
						}
					}

					this.addToRecent(muni, this.cwpeData)
				})
				.catch(err => {
					this.lookupError = err?.response?.data?.error || 'Lookup failed. Please try again.'
				})

			const internalPromise = restApi.get(`/cwpe/lookup/internal-data/${muni.id}`)
				.then(({ data }) => { this.internalData = data })
				.catch(() => { this.internalData = null })
				.finally(() => { this.internalDataLoading = false })

			const peerPromise = restApi.get(`/cwpe/lookup/peer-pricing/${muni.id}`)
				.then(({ data }) => { this.peerPricing = data })
				.catch(() => { this.peerPricing = null })
				.finally(() => { this.peerPricingLoading = false })

			Promise.allSettled([pricingPromise, internalPromise, peerPromise])
				.finally(() => {
					this.loading = false
					// Auto-capture a homepage screenshot once we have a website
					// and nothing is stored yet. Background work — silent on
					// success or failure (no toast). Skips when a thumbnail is
					// already on cwpe_municipality_data so we don't redo it on
					// every navigation.
					if (this.effectiveWebsite && !this.cwpeData?.thumbnail_url) {
						this.captureThumbnail(true)
					}
				})
		},

		lookAgain() {
			if (!this.selectedMunicipality) return
			const cur = this.cwpeData?.website_url
			if (cur && !this.rejectedUrls.includes(cur)) {
				this.rejectedUrls = [...this.rejectedUrls, cur]
			}
			this.lookingAgain = true
			restApi.post('/cwpe/pricing/lookup', {
				municipality_id: this.selectedMunicipality.id,
				name: this.form.name,
				state: this.form.state,
				force_refresh: true,
			})
				.then(({ data }) => {
					this.cwpeData = data.municipality_data
					this.pricingResult = data.pricing
				})
				.catch(() => {})
				.finally(() => { this.lookingAgain = false })
		},

		// ── Reprice ───────────────────────────────────────────────────
		reprice() {
			this.saveQuoteCtx()
			this.persistPriorAnnual()
			if (!this.cwpeData) return
			restApi.post('/cwpe/pricing/simulate', {
				population:            this.cwpeData.population,
				population_trend_pct:  this.cwpeData.population_trend_pct,
				county_trend_pct:      this.countyTrend,
				state_trend_pct:       this.stateTrend,
				snap_rate:             this.cwpeData.snap_rate,
				state:                 this.form.state,
				mode:                  this.mode,
				contract_years:        this.contractYears,
				prior_year_annual_cents: this.mode === 'upgrade' && this.priorAnnualDollars
					? Math.round(this.priorAnnualDollars * 100) : null,
				target_increase_pct: this.mode === 'upgrade' ? this.targetIncreasePct / 100 : null,
			})
				.then(({ data }) => { this.pricingResult = data })
				.catch(() => {})
		},

		saveQuoteCtx() {
			localStorage.setItem(QUOTE_CTX_KEY, JSON.stringify({
				mode:               this.mode,
				contractYears:      this.contractYears,
				priorAnnualDollars: this.priorAnnualDollars,
				targetIncreasePct:  this.targetIncreasePct,
			}))
		},
		setMode(m) { this.mode = m; this.saveQuoteCtx(); this.reprice() },
		setContractYears(y) { this.contractYears = y; this.saveQuoteCtx(); this.reprice() },

		clearPriorAnnual() {
			this.priorAnnualDollars = null
			this.uploadInfo = null
			this.uploadError = null
			this.saveQuoteCtx()
			this.reprice()
			this.persistPriorAnnual()
		},

		// Persist prior year annual to the DB so Pricing page can auto-populate
		persistPriorAnnual() {
			if (!this.selectedMuniId) return
			restApi.post('/cwpe/pricing/municipality-data', {
				municipality_id: this.selectedMuniId,
				prior_year_annual_cents: this.priorAnnualDollars != null
					? Math.round(this.priorAnnualDollars * 100) : null,
			}).catch(() => { /* ignore */ })
		},

		// ── Bundle ────────────────────────────────────────────────────
		pickBundle(bundleId) {
			this.selectedBundleId = this.selectedBundleId === bundleId ? null : bundleId
			try {
				if (this.selectedBundleId) localStorage.setItem(BUNDLE_KEY, this.selectedBundleId)
				else localStorage.removeItem(BUNDLE_KEY)
			} catch (_) { /* ignore */ }
		},

		// ── Invoice upload ────────────────────────────────────────────
		pickInvoiceFile() {
			this.$refs.invoiceFileEl.click()
		},

		onInvoiceFilePicked(e) {
			const file = e.target.files?.[0]
			if (!file) return
			e.target.value = ''
			if (!this.selectedMunicipality) return

			const formData = new FormData()
			formData.append('file', file)
			formData.append('municipality_id', this.selectedMunicipality.id)

			this.uploading = true
			this.uploadError = null
			this.uploadInfo = null
			restApi.post('/cwpe/proposals/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
				.then(({ data }) => {
					const annual = data?.parsed_data?.priorYearAnnualCents
					const term = data?.parsed_data?.contractYears
					if (annual) {
						this.priorAnnualDollars = annual / 100
						if (term) this.contractYears = term
						if (this.mode !== 'upgrade') this.mode = 'upgrade'
						this.saveQuoteCtx()
						this.reprice()
					}
					this.uploadInfo = `Parsed "${file.name}" — prior annual $${((annual || 0) / 100).toLocaleString()}${term ? `, ${term}-yr term` : ''}.`
				})
				.catch(err => {
					this.uploadError = err?.response?.data?.message || 'Parse failed.'
				})
				.finally(() => { this.uploading = false })
		},

		// ── Recent ────────────────────────────────────────────────────
		addToRecent(muni, cwpeData) {
			const item = {
				id: muni.id,
				name: muni.full_name || muni.name,
				state: this.form.state || muni.state,
				websiteUrl: cwpeData?.website_url || muni.website || null,
				thumbnailUrl: cwpeData?.thumbnail_url || null,
			}
			const list = this.recent.filter(r => r.id !== item.id)
			list.unshift(item)
			this.recent = list.slice(0, 10)
			localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent))
		},

		openRecent(r) {
			const muni = {
				id: r.id,
				full_name: r.name,
				name: r.name,
				state: r.state,
				website: r.websiteUrl || null,
			}
			this.selectedMunicipality = muni
			this.selectedMuniId = muni.id
			this.form.name = r.name
			this.form.state = r.state
			this.form.url = r.websiteUrl || ''
			this.loadCwpeData(muni)
		},

		// ── Helpers ───────────────────────────────────────────────────
		hostnameOf(url) {
			if (!url) return ''
			try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
		},

		// Mirror the URL patterns mc2 uses in LPDataTableTbody.vue / ClientEdit.vue
		// so the CWPE deep-links land on the same screens reps already use.
		salesforceUrl(accountId) {
			return `https://hejdevdoo.lightning.force.com/lightning/r/Account/${accountId}/view`
		},
		freshbooksUrl(clientId) {
			// mc2 has no existing FB deep-link pattern; FB's own app uses
			// /#/client/{id} (singular) when logged in. Once an account-scoped
			// pattern is confirmed, swap this single line.
			return `https://my.freshbooks.com/#/client/${clientId}`
		},
		dropboxUrl(dropboxId) {
			const email = (this.authUser && this.authUser.email) || ''
			return `${this.dropboxAppUrl}/dropbox/${dropboxId}/files?email=${encodeURIComponent(email)}&sauto=soflogin`
		},

		normalizeUrl(url) {
			if (!url) return null
			const trimmed = String(url).trim()
			if (!trimmed) return null
			if (/^https?:\/\//i.test(trimmed)) return trimmed
			return `https://${trimmed}`
		},

		// Capture a homepage thumbnail. Backend proxies to the screenshotter
		// Cloudflare Worker (Browser Rendering API), saves the PNG to
		// storage/app/public/cwpe-thumbnails/, and returns a public URL. The
		// URL is persisted on cwpe_municipality_data.thumbnail_url so it
		// survives reloads. `auto=true` suppresses the toast on auto-capture.
		captureThumbnail(auto = false) {
			if (!this.effectiveWebsite || !this.selectedMunicipality) return
			this.capturingThumbnail = true
			this.captureMsg = null

			restApi.post('/cwpe/pricing/capture-screenshot', {
				municipality_id: this.selectedMunicipality.id,
				url:             this.effectiveWebsite,
			}).then(({ data }) => {
				const thumbUrl = data && data.thumbnail_url
				if (!thumbUrl) throw new Error('No thumbnail URL returned.')
				// Patch local cwpeData so the existing <img v-if=…thumbnail_url>
				// renders the new screenshot immediately, without re-lookup.
				if (this.cwpeData) {
					this.cwpeData = { ...this.cwpeData, thumbnail_url: thumbUrl }
				} else {
					this.cwpeData = { thumbnail_url: thumbUrl }
				}
				// Refresh the Recent sidebar entry too if it's for this muni.
				const idx = this.recent.findIndex(r => r.id === this.selectedMunicipality.id)
				if (idx >= 0) {
					this.recent[idx] = { ...this.recent[idx], thumbnailUrl: thumbUrl }
					try { localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent)) } catch (_) { /* ignore quota */ }
				}
				if (!auto) {
					this.captureMsg = 'Preview captured.'
					this.captureMsgTone = 'ok'
				}
			}).catch(err => {
				const body = err && err.response && err.response.data
				const msg = (body && (body.error || body.message)) || 'Capture failed.'
				if (auto) {
					// eslint-disable-next-line no-console
					console.warn('CWPE auto-capture failed:', msg)
				} else {
					this.captureMsg = msg
					this.captureMsgTone = 'err'
				}
			}).finally(() => {
				this.capturingThumbnail = false
				if (!auto) {
					setTimeout(() => { this.captureMsg = null }, 5000)
				}
			})
		},

		contactName(c) {
			const n = `${c.first_name || ''} ${c.last_name || ''}`.trim()
			return n || c.email || '—'
		},
		fmtCompactDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},
		// Percentage formatter — accepts 0.025 → "2.5%"; null/NaN → "—".
		fmtPct(d) {
			if (d == null || isNaN(d)) return '—'
			return (d * 100).toFixed(1) + '%'
		},
		// Color helper for trend values — green when growing, red when shrinking
		// relative to the comparison baseline (0 by default).
		trendValueColor(value, vs) {
			if (value == null) return 'color:#475569;'
			if (value > vs) return 'color:#047857;'
			if (value < vs) return 'color:#be123c;'
			return 'color:#475569;'
		},
		fmtRelative(val) {
			if (!val) return '—'
			const dt = new Date(val)
			const days = Math.round((Date.now() - dt.getTime()) / 86400000)
			if (days < 1) return 'today'
			if (days === 1) return 'yesterday'
			if (days < 30) return `${days}d ago`
			if (days < 365) return `${Math.round(days / 30)}mo ago`
			return `${Math.round(days / 365)}y ago`
		},
		adaScoreColor(score) {
			if (score == null) return '#64748b'
			if (score >= 90) return '#15803d'
			if (score >= 70) return '#b45309'
			return '#be123c'
		},
		campaignTypeLabel(t) {
			if (!t) return '—'
			const s = String(t).toLowerCase().trim()
			if (s === 'postcard' || s === 'postcards') return 'Postcard'
			if (s === 'letter' || s === 'letters') return 'Letter'
			return s.charAt(0).toUpperCase() + s.slice(1)
		},
		deliveryStatusColor(status) {
			const s = String(status || '').toLowerCase()
			if (s.includes('deliver')) return '#047857'
			if (s.includes('process') || s.includes('mailed') || s.includes('transit')) return '#1d4ed8'
			if (s.includes('return') || s.includes('fail') || s.includes('error')) return '#be123c'
			if (s.includes('cancel')) return '#92400e'
			return '#64748b'
		},

		bundleDesc(id) { return BUNDLE_DESC[id] || '' },
		bundleIncludes(id) { return BUNDLE_INCLUDES[id] || [] },

		cents(val) {
			if (val == null) return '—'
			return '$' + (val / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })
		},
		// "Closest" peer sanity check — if the nearest-by-population TW client
		// is more than ~2× off (too big or too small) it isn't a real
		// comparable. The rep should treat it as a rough reference, not a price
		// anchor, so we badge it "stretch" and show a warning instead of "closest".
		peerTooFar(peer) {
			const target = Number(this.peerPricing?.target?.population)
			const pop = Number(peer?.population)
			if (!target || !pop) return false
			return pop > target * 2 || pop < target / 2
		},
		pct(val, digits = 1) {
			if (val == null) return '—'
			return (parseFloat(val) * 100).toFixed(digits) + '%'
		},
		fmt(val) {
			if (val == null) return '—'
			return Number(val).toLocaleString('en-US')
		},
		trendArrow(val) {
			if (val == null) return '·'
			return parseFloat(val) >= 0 ? '▲' : '▼'
		},
		trendColor(val) {
			if (val == null) return 'cwpe-trendcolor-neutral'
			return parseFloat(val) >= 0 ? 'cwpe-trendcolor-pos' : 'cwpe-trendcolor-neg'
		},
		adjClass(val) {
			if (val == null || val === 0) return 'cwpe-adj-neutral'
			return val > 0 ? 'cwpe-adj-pos' : 'cwpe-adj-neg'
		},
		formatDate(val) {
			if (!val) return ''
			return new Date(val).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })
		},
	},
}
</script>

<style scoped>
/* ── Form labels ────────────────────────────────────────────────── */
.cwpe-form-label {
	display: block;
	font-size: 11px;
	font-weight: 600;
	color: #475569;
	text-transform: none;
	margin-bottom: 4px;
}
.cwpe-label {
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #64748b;
}
.cwpe-label-amber {
	color: #92400e !important;
}
.cwpe-sublabel {
	font-size: 10px;
	color: #94a3b8;
	line-height: 1.3;
}

/* ── Municipality autocomplete suggestion ───────────────────────── */
.cwpe-suggestion-item {
	transition: background 0.08s;
}
.cwpe-suggestion-item:hover,
.cwpe-suggestion-active {
	background-color: #eff6ff;
}

/* ── Recent sidebar button ──────────────────────────────────────── */
.cwpe-recent-btn {
	transition: background 0.1s;
	line-height: 1.3;
}
.cwpe-recent-btn:hover {
	background-color: #eff6ff !important;
}

/* ── Look again button hover ────────────────────────────────────── */
.cwpe-look-again-btn:hover {
	text-decoration: underline !important;
}

/* ── Website link hover ─────────────────────────────────────────── */
.cwpe-website-link:hover {
	text-decoration: underline !important;
}

/* ── Tier badge colors ──────────────────────────────────────────── */
.cwpe-tier-entry {
	background: #f1f5f9;
	color: #1e293b;
	box-shadow: inset 0 0 0 1px #cbd5e1;
}
.cwpe-tier-value {
	background: #e0f2fe;
	color: #075985;
	box-shadow: inset 0 0 0 1px #7dd3fc;
}
.cwpe-tier-standard {
	background: #d1fae5;
	color: #065f46;
	box-shadow: inset 0 0 0 1px #a7f3d0;
}
.cwpe-tier-premium {
	background: #ede9fe;
	color: #5b21b6;
	box-shadow: inset 0 0 0 1px #c4b5fd;
}
.cwpe-tier-enterprise {
	background: #dbeafe;
	color: #1e3a8a;
	box-shadow: inset 0 0 0 1px #93c5fd;
}

/* ── Quote context bar buttons ──────────────────────────────────── */
.cwpe-btn-group {
	border-color: #d1d5db !important;
}
.cwpe-ctx-btn {
	padding: 4px 12px;
	font-size: 13px;
	font-weight: 500;
	background: white;
	border: none;
	cursor: pointer;
	transition: background 0.12s, color 0.12s;
}
.cwpe-ctx-btn-idle { color: #4b5563; }
.cwpe-ctx-btn-idle:hover { background: #f3f4f6; }
.cwpe-ctx-btn-primary { background: #2563eb; color: white; }
.cwpe-ctx-btn-upgrade { background: #d97706; color: white; }
.cwpe-ctx-btn-dark { background: #1e293b; color: white; }

/* Upgrade input style */
.cwpe-dollar-sign {
	font-size: 13px;
	pointer-events: none;
	line-height: 1;
	/* left: 8px is handled by ps-2 utility */
}
.cwpe-upgrade-input {
	background-color: rgba(251, 191, 36, 0.12);
	border-color: #fcd34d; /* amber-300 */
	font-size: 13px;
}
.cwpe-upgrade-input:focus {
	background-color: rgba(251, 191, 36, 0.18);
	border-color: #d97706;
	box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.18);
	outline: none;
}

.cwpe-upload-btn {
	border: 1px solid #f59e0b;
	color: #78350f;
	background: white;
	white-space: nowrap;
	font-size: 12px;
	font-weight: 500;
}
.cwpe-upload-btn:hover { background: #fffbeb; }
.cwpe-upload-btn:disabled { opacity: 0.5; }

/* Range slider — amber accent */
.cwpe-range {
	accent-color: #d97706;
	cursor: pointer;
	display: block;
	width: 100%;
}

/* +X% badge next to slider */
.cwpe-pct-badge {
	min-width: 2.8rem;
	text-align: right;
	color: #c2410c;    /* orange-700 — matches screenshot bright orange */
	font-size: 16px;
	font-weight: 700;
	font-family: ui-monospace, monospace;
	letter-spacing: -0.01em;
	white-space: nowrap;
}

/* ── Bundle cards ───────────────────────────────────────────────── */
.cwpe-bundle-card {
	border: 2px solid transparent;
	transition: border-color 0.12s, box-shadow 0.12s;
	cursor: pointer;
}
.cwpe-bundle-card:hover {
	box-shadow: 0 4px 16px rgba(0,0,0,0.10);
}
.cwpe-bundle-selected {
	border-color: #2563eb !important;
	background-color: #eff6ff !important;
	box-shadow: 0 0 0 3px rgba(37,99,235,0.15) !important;
}
.cwpe-bundle-recommended {
	border-color: #93c5fd !important;
	background-color: rgba(239,246,255,0.4) !important;
}
.cwpe-bundle-default {
	border-color: #e2e8f0 !important;
	background-color: white !important;
	opacity: 0.8;
}
.cwpe-bundle-default:hover {
	opacity: 1;
	border-color: #94a3b8 !important;
}

/* ── Adjustment factor pills ────────────────────────────────────── */
.cwpe-pill-pos {
	background: #ecfdf5;
	color: #065f46;
	box-shadow: inset 0 0 0 1px #a7f3d0;
}
.cwpe-pill-neg {
	background: #fff1f2;
	color: #9f1239;
	box-shadow: inset 0 0 0 1px #fecdd3;
}
.cwpe-pill-neutral {
	background: #f1f5f9;
	color: #334155;
	box-shadow: inset 0 0 0 1px #e2e8f0;
}

/* ── Adjustment amount colors ───────────────────────────────────── */
.cwpe-adj-pos { color: #047857; }
.cwpe-adj-neg { color: #be123c; }
.cwpe-adj-neutral { color: #64748b; }

/* ── SNAP rate colors ───────────────────────────────────────────── */
.cwpe-snap-high { color: #be123c; }
.cwpe-snap-low  { color: #047857; }
.cwpe-snap-mid  { color: #b45309; }

/* ── Trend colors ───────────────────────────────────────────────── */
.cwpe-trendcolor-pos { color: #047857; }
.cwpe-trendcolor-neg { color: #be123c; }
.cwpe-trendcolor-neutral { color: #64748b; }

/* ── Thumbnail button ───────────────────────────────────────────── */
.cwpe-thumb-btn {
	transition: opacity 0.15s;
}
.cwpe-thumb-btn:hover {
	opacity: 0.92;
}

/* ── Loading skeleton ───────────────────────────────────────────── */
.cwpe-skel {
	background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
	background-size: 200% 100%;
	border-radius: 4px;
	animation: cwpe-skel-shimmer 1.4s ease-in-out infinite;
}
@keyframes cwpe-skel-shimmer {
	0%   { background-position: 200% 0; }
	100% { background-position: -200% 0; }
}
.cwpe-skeleton-card {
	min-height: 420px;
}
</style>
